import { useEffect, useMemo, useState, useCallback, useRef, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { computeShift, kes, ltr, shiftLabel, STATUS_LABELS, mpesaCode, shiftStartISO, pricesAt } from "@/lib/format";
import { StatusBadge, VarianceBadge } from "@/components/StatusBadge";
import AuditTrailDrawer from "@/components/AuditTrailDrawer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Send, Lock, Unlock, CheckCircle2, ClipboardCheck, History,
  FileSpreadsheet, FileText, Loader2, Plus, Fuel, Gauge, Package, Wallet, Receipt, TriangleAlert, ShieldAlert, Smartphone, Truck,
  Landmark,
} from "lucide-react";
import { isAccountant as canAccount, isManager as canManage } from "@/lib/roles";

const LOCKED_EDIT = (status) => status !== "draft";

// Spreadsheet-style arrow-key navigation between data-entry cells, scoped to each <table>.
// Up/Down moves to the same column in the previous/next row; Left/Right (number fields only,
// so text-field cursor movement still works) moves to the adjacent cell in the same row.
function handleGridKeyDown(e) {
  const key = e.key;
  if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter"].includes(key)) return;
  const target = e.target;
  if (target.tagName !== "INPUT") return;
  if ((key === "ArrowLeft" || key === "ArrowRight") && target.type !== "number") return;

  const table = target.closest("table");
  if (!table) return;
  const rows = Array.from(table.querySelectorAll("tbody tr"))
    .map((tr) => Array.from(tr.querySelectorAll("input:not([disabled])")))
    .filter((cells) => cells.length > 0);

  let ri = -1, ci = -1;
  for (let r = 0; r < rows.length; r++) {
    const idx = rows[r].indexOf(target);
    if (idx !== -1) { ri = r; ci = idx; break; }
  }
  if (ri === -1) return;

  let dest = null;
  if (key === "ArrowDown" || key === "Enter") {
    if (ri + 1 < rows.length) dest = rows[ri + 1][Math.min(ci, rows[ri + 1].length - 1)];
  } else if (key === "ArrowUp") {
    if (ri - 1 >= 0) dest = rows[ri - 1][Math.min(ci, rows[ri - 1].length - 1)];
  } else if (key === "ArrowRight") {
    if (ci + 1 < rows[ri].length) dest = rows[ri][ci + 1];
  } else if (key === "ArrowLeft") {
    if (ci - 1 >= 0) dest = rows[ri][ci - 1];
  }
  if (dest) {
    e.preventDefault();
    dest.focus();
    dest.select?.();
  }
}

export default function ShiftEntry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [shift, setShift] = useState(null);
  const [entry, setEntry] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState(null); // last failed save, shown until a save succeeds
  const saveErrorRef = useRef(null);
  saveErrorRef.current = saveError;
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [transition, setTransition] = useState(null); // {action, title}
  const [note, setNote] = useState("");
  const [emailOnSubmit, setEmailOnSubmit] = useState(true);

  const load = useCallback(async () => {
    const [cfgRes, shiftRes] = await Promise.all([api.get("/config"), api.get(`/shifts/${id}`)]);
    setCfg(cfgRes.data);
    setShift(shiftRes.data);
    // build editable entry from shift or seed rows from config
    const s = shiftRes.data;
    const c = cfgRes.data;
    const activePumps = c.pumps.filter((p) => p.active !== false);
    const activeTanks = c.tanks.filter((t) => t.active !== false);
    // spares (Kariokor's filters & air cleaners) are sold from the store, not counted per shift
    const activeItems = c.nonfuel_items.filter((i) => i.active !== false && !["spare", "service"].includes(i.category));
    const activeMethods = c.payment_methods.filter((m) => m.active !== false);

    const fuelMap = Object.fromEntries((s.fuel_readings || []).map((r) => [String(r.pump_id), r]));
    const tankMap = Object.fromEntries((s.tank_dips || []).map((r) => [String(r.tank_id), r]));
    const itemMap = Object.fromEntries((s.nonfuel || []).map((r) => [String(r.item_id), r]));
    const payMap = Object.fromEntries((s.payments || []).map((r) => [`${r.attendant_id || ""}|${r.method_id}`, r]));
    const activeAttendants = c.attendants.filter((a) => a.active !== false);
    const payGroups = [...activeAttendants.map((a) => a.id), ""]; // "" = Unassigned / Shop
    const activeTills = (c.tills || []).filter((t) => t.active !== false);
    const tillMap = Object.fromEntries((s.till_ledger || []).map((r) => [String(r.till_id), r]));

    // Prices are set in Station Setup, not editable per-shift. A draft takes the price in force when
    // the shift started (a change recorded mid-shift, or set to start later, waits for that shift);
    // once submitted/locked the shift can no longer be saved, so its prices stay frozen as history.
    const fuel_prices = { ...(s.fuel_prices || {}) };
    const fuel_cost_prices = { ...(s.fuel_cost_prices || {}) };
    // A shift with locked prices (e.g. a one-shift promo) keeps its own prices instead.
    if (!LOCKED_EDIT(s.status) && !s.fuel_prices_locked) {
      try {
        const pr = await api.get("/config/fuel-prices");
        const at = pricesAt(pr.data, shiftStartISO(s.date, s.shift_type, c.station?.shift_hours));
        Object.assign(fuel_prices, at.price);
        Object.assign(fuel_cost_prices, at.cost);
      } catch (e) { console.debug("fuel price refresh skipped", e); }
    }

    setEntry({
      supervisor_name: s.supervisor_name || "",
      fuel_prices,
      fuel_cost_prices,
      pump_attendants: { ...(s.pump_attendants || {}) },
      // Vivo-reported fuel sales per attendant (a key with "" = on shift, figure not entered yet)
      attendant_expected: { ...(s.attendant_expected || {}) },
      attendant_lube_sales: { ...(s.attendant_lube_sales || {}) },
      till_attendants: { ...(s.till_attendants || {}) },
      nonfuel_group_attendants: { ...(s.nonfuel_group_attendants || {}) },
      // Lubes & LPG collections: one row per person / group / method (rows from the old fixed
      // grid with no amount are dropped; an old group-level collector is carried onto its rows)
      nonfuel_payments: (s.nonfuel_payments || [])
        .filter((p) => num(p.amount) || p.attendant_id)
        .map((p) => ({
          _k: p._k || uid(), attendant_id: p.attendant_id || s.nonfuel_group_attendants?.[p.group] || "",
          group: p.group || "lube", method_id: p.method_id || "", amount: p.amount ?? "", reference: p.reference || "", till_id: p.till_id || "",
        })),
      till_ledger: activeTills.map((t) => ({
        till_id: t.id,
        opening_balance: tillMap[t.id]?.opening_balance ?? "", withdrawal: tillMap[t.id]?.withdrawal ?? "",
        actual_balance: tillMap[t.id]?.actual_balance ?? "",
        actual_balance_at: tillMap[t.id]?.actual_balance_at ?? "",
        _openingLocked: hasValue(tillMap[t.id]?.opening_balance),
      })),
      fuel_readings: activePumps.map((p) => ({
        pump_id: p.id, product_id: p.product_id,
        opening: fuelMap[p.id]?.opening ?? "", closing: fuelMap[p.id]?.closing ?? "", testing: fuelMap[p.id]?.testing ?? "",
        elec_opening_l: fuelMap[p.id]?.elec_opening_l ?? "", elec_closing_l: fuelMap[p.id]?.elec_closing_l ?? "",
        elec_opening_kes: fuelMap[p.id]?.elec_opening_kes ?? "", elec_closing_kes: fuelMap[p.id]?.elec_closing_kes ?? "",
        _openingLocked: hasValue(fuelMap[p.id]?.opening),
      })),
      tank_dips: activeTanks.map((t) => ({
        tank_id: t.id, product_id: t.product_id,
        opening_dip: tankMap[t.id]?.opening_dip ?? "", deliveries: tankMap[t.id]?.deliveries ?? "",
        rtt: tankMap[t.id]?.rtt ?? "", closing_dip: tankMap[t.id]?.closing_dip ?? "",
        tls_opening_dip: tankMap[t.id]?.tls_opening_dip ?? "", tls_closing_dip: tankMap[t.id]?.tls_closing_dip ?? "",
        _openingLocked: hasValue(tankMap[t.id]?.opening_dip),
        _tlsOpeningLocked: hasValue(tankMap[t.id]?.tls_opening_dip),
      })),
      nonfuel: activeItems.map((it) => ({
        // lube & LPG prices come from Station Setup → Items (a draft follows them; a submitted shift keeps its own)
        item_id: it.id,
        unit_price: LOCKED_EDIT(s.status) ? (itemMap[it.id]?.unit_price ?? it.unit_price) : it.unit_price,
        cost_price: LOCKED_EDIT(s.status) ? (itemMap[it.id]?.cost_price ?? it.cost_price) : it.cost_price,
        opening: itemMap[it.id]?.opening ?? "", received: itemMap[it.id]?.received ?? "", returned: itemMap[it.id]?.returned ?? "", closing: itemMap[it.id]?.closing ?? "",
        shop_opening: itemMap[it.id]?.shop_opening ?? "", shop_closing: itemMap[it.id]?.shop_closing ?? "",
        _openingLocked: hasValue(itemMap[it.id]?.opening),
        _shopOpeningLocked: hasValue(itemMap[it.id]?.shop_opening),
      })),
      payments: payGroups.flatMap((attId) =>
        activeMethods.map((m) => {
          const key = `${attId}|${m.id}`;
          return { attendant_id: attId, method_id: m.id, amount: payMap[key]?.amount ?? "", reference: payMap[key]?.reference ?? "" };
        })
      ),
      expenses: (s.expenses || []).map((e) => ({ ...e, _k: e._k || uid() })),
      till_transfers: (s.till_transfers || []).map((t) => ({ ...t, _k: t._k || uid() })),
      account_receipts: (s.account_receipts || []).map((a) => ({ ...a, _k: a._k || uid() })),
      invoice_lines: (s.invoice_lines || []).map((l) => ({ ...l, _k: l._k || uid() })),
      payment_transactions: (s.payment_transactions || []).map((t) => ({ ...t, _k: t._k || uid() })),
      tank_deliveries: (s.tank_deliveries || []).map((d) => ({ ...d, _k: d._k || uid() })),
      nonfuel_deliveries: (s.nonfuel_deliveries || []).map((d) => ({ ...d, _k: d._k || uid() })),
      store_sales: (s.store_sales || []).map((x) => ({ ...x, _k: x._k || uid(), till_id: x.till_id || "", attendant_id: x.attendant_id || "" })),
      notes: s.notes || "",
    });
    setDirty(false);
  }, [id]);

  useEffect(() => {
    load().catch((e) => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      navigate("/");
    });
  }, [load, navigate]);

  const computed = useMemo(() => {
    if (!cfg || !entry) return null;
    return computeShift(
      {
        fuel_prices: entry.fuel_prices,
        fuel_cost_prices: entry.fuel_cost_prices,
        fuel_readings: entry.fuel_readings.map((r) => ({
          ...r, opening: num(r.opening), closing: num(r.closing), testing: num(r.testing),
          elec_opening_l: num(r.elec_opening_l), elec_closing_l: num(r.elec_closing_l),
          elec_opening_kes: num(r.elec_opening_kes), elec_closing_kes: num(r.elec_closing_kes),
        })),
        tank_dips: entry.tank_dips.map((r) => ({ ...r, opening_dip: num(r.opening_dip), deliveries: num(r.deliveries), rtt: num(r.rtt), closing_dip: num(r.closing_dip), tls_opening_dip: num(r.tls_opening_dip), tls_closing_dip: num(r.tls_closing_dip) })),
        tank_deliveries: entry.tank_deliveries.map((d) => ({
          ...d, invoice_amount: num(d.invoice_amount), invoice_volume: num(d.invoice_volume), offloaded_quantity: num(d.offloaded_quantity),
        })),
        nonfuel: entry.nonfuel.map((r) => ({ ...r, opening: num(r.opening), received: num(r.received), returned: num(r.returned), closing: num(r.closing), shop_opening: num(r.shop_opening), shop_closing: num(r.shop_closing), unit_price: num(r.unit_price), cost_price: num(r.cost_price) })),
        nonfuel_deliveries: entry.nonfuel_deliveries.map((d) => ({
          ...d, invoice_amount: num(d.invoice_amount), invoice_quantity: num(d.invoice_quantity), actual_quantity: num(d.actual_quantity),
        })),
        payments: entry.payments.map((r) => ({ ...r, amount: num(r.amount) })),
        payment_transactions: entry.payment_transactions.map((t) => ({ ...t, amount: num(t.amount) })),
        expenses: entry.expenses.map((e) => ({ ...e, amount: num(e.amount) })),
        till_transfers: (entry.till_transfers || []).map((t) => ({ ...t, amount: num(t.amount) })),
        account_receipts: (entry.account_receipts || []).map((a) => ({ ...a, amount: num(a.amount) })),
        invoice_lines: entry.invoice_lines.map((l) => ({ ...l, amount: num(l.amount), litres: num(l.litres) })),
        pump_attendants: entry.pump_attendants,
        attendant_expected: entry.attendant_expected,
        attendant_lube_sales: entry.attendant_lube_sales,
        till_attendants: entry.till_attendants,
        till_ledger: entry.till_ledger.map((t) => ({ ...t, opening_balance: num(t.opening_balance), withdrawal: num(t.withdrawal), actual_balance: t.actual_balance === "" ? null : num(t.actual_balance) })),
        nonfuel_group_attendants: entry.nonfuel_group_attendants,
        nonfuel_payments: entry.nonfuel_payments.map((p) => ({ ...p, amount: num(p.amount), till_id: p.till_id || null })),
        store_sales: (entry.store_sales || []).map((x) => {
          const it = cfg.nonfuel_items.find((i) => i.id === x.item_id) || {};
          const service = it.category === "service";
          return { ...x, qty: num(x.qty), unit_price: service ? num(x.unit_price) : num(it.unit_price), cost_price: service ? 0 : num(it.cost_price),
            till_id: x.till_id || null, attendant_id: x.attendant_id || null };
        }),
        mpesa_api_by_till: shift?.mpesa_api_by_till || {},
        vivo_includes_nonfuel: !!shift?.vivo_includes_nonfuel,
        mpesa_api_live: !!shift?.mpesa_api_live,
        mpesa_code_lookup: shift?.mpesa_code_lookup || {},
      },
      cfg
    );
  }, [cfg, entry, shift]);

  // Autosave: without this, a page closed right after typing loses everything, since data only
  // lives in this component's state until "Save Draft" is clicked. Debounce persists ~1.5s after
  // the user stops typing; visibilitychange/beforeunload cover the tab-closed-immediately case.
  // Hooks must run unconditionally (before the loading guard below), so they read live values via
  // refs — saveRef is pointed at the real save() function further down, once it's defined.
  const saveRef = useRef(() => {});
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!dirty || !shift || LOCKED_EDIT(shift.status)) return;
    const timer = setTimeout(() => { saveRef.current({ silent: true }); }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, dirty, shift]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden" && dirtyRef.current) saveRef.current({ silent: true });
    };
    const onBeforeUnload = (e) => {
      if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; }
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  if (!cfg || !entry || !shift || !computed) {
    return <div className="flex py-20 justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const readOnly = LOCKED_EDIT(shift.status);
  const isManager = canManage(user);
  const isAccountant = canAccount(user);

  const set = (path) => (val) => {
    setEntry((prev) => {
      const next = structuredClone(prev);
      path(next, val);
      return next;
    });
    setDirty(true);
  };

  const buildPayload = () => ({
    supervisor_name: entry.supervisor_name,
    fuel_prices: Object.fromEntries(Object.entries(entry.fuel_prices).map(([k, v]) => [k, num(v)])),
    fuel_cost_prices: Object.fromEntries(Object.entries(entry.fuel_cost_prices || {}).map(([k, v]) => [k, num(v)])),
    pump_attendants: entry.pump_attendants || {},
    attendant_expected: Object.fromEntries(Object.entries(entry.attendant_expected || {}).map(([k, v]) => [k, v === "" ? "" : num(v)])),
    attendant_lube_sales: Object.fromEntries(Object.entries(entry.attendant_lube_sales || {}).map(([k, v]) => [k, num(v)])),
    till_attendants: entry.till_attendants || {},
    till_ledger: entry.till_ledger.map((t) => ({
      till_id: t.till_id, opening_balance: num(t.opening_balance), withdrawal: num(t.withdrawal),
      actual_balance: t.actual_balance === "" ? null : num(t.actual_balance), actual_balance_at: t.actual_balance_at || null,
    })),
    nonfuel_group_attendants: entry.nonfuel_group_attendants || {},
    nonfuel_payments: entry.nonfuel_payments.map((p) => ({
      _k: p._k, attendant_id: p.attendant_id || null, group: p.group, method_id: p.method_id,
      amount: num(p.amount), reference: p.reference || "", till_id: p.till_id || null,
    })),
    fuel_readings: entry.fuel_readings.map((r) => ({
      pump_id: r.pump_id, product_id: r.product_id, opening: num(r.opening), closing: num(r.closing), testing: num(r.testing),
      elec_opening_l: num(r.elec_opening_l), elec_closing_l: num(r.elec_closing_l),
      elec_opening_kes: num(r.elec_opening_kes), elec_closing_kes: num(r.elec_closing_kes),
    })),
    tank_dips: entry.tank_dips.map((r) => ({ tank_id: r.tank_id, product_id: r.product_id, opening_dip: num(r.opening_dip), deliveries: num(r.deliveries), rtt: num(r.rtt), closing_dip: num(r.closing_dip), tls_opening_dip: num(r.tls_opening_dip), tls_closing_dip: num(r.tls_closing_dip) })),
    tank_deliveries: entry.tank_deliveries.map((d) => ({
      _k: d._k, tank_id: d.tank_id, invoice_no: d.invoice_no || "", date: d.date || "",
      product_id: d.product_id || null, invoice_amount: num(d.invoice_amount),
      invoice_volume: num(d.invoice_volume), offloaded_quantity: num(d.offloaded_quantity),
    })),
    nonfuel: entry.nonfuel.map((r) => ({ item_id: r.item_id, opening: num(r.opening), received: num(r.received), returned: num(r.returned), closing: num(r.closing),
      ...(cfg?.station?.lube_shop_front ? { shop_opening: num(r.shop_opening), shop_closing: num(r.shop_closing) } : {}), unit_price: num(r.unit_price), cost_price: num(r.cost_price) })),
    store_sales: (entry.store_sales || []).filter((x) => x.item_id || num(x.qty)).map((x) => ({
      _k: x._k, item_id: x.item_id, qty: num(x.qty), unit_price: num(x.unit_price), method_id: x.method_id, till_id: x.till_id || null,
      attendant_id: x.attendant_id || null, reference: x.reference || "",
    })),
    nonfuel_deliveries: entry.nonfuel_deliveries.map((d) => ({
      _k: d._k, item_id: d.item_id, invoice_no: d.invoice_no || "", date: d.date || "",
      invoice_amount: num(d.invoice_amount), invoice_quantity: num(d.invoice_quantity), actual_quantity: num(d.actual_quantity),
    })),
    payments: entry.payments.map((r) => ({ attendant_id: r.attendant_id || null, method_id: r.method_id, amount: num(r.amount), reference: r.reference })),
    payment_transactions: entry.payment_transactions.map((t) => ({ attendant_id: t.attendant_id || null, method_id: t.method_id, amount: num(t.amount), reference: t.reference || "", note: t.note || "",
      ...((cfg?.payment_methods || []).find((m) => m.id === t.method_id)?.kind === "card" ? { txn_date: t.txn_date || "", auth_code: t.auth_code || "", product: t.product || "" } : {}) })),
    expenses: entry.expenses.map((e) => ({ ...e, amount: num(e.amount) })),
    till_transfers: (entry.till_transfers || []).map((t) => ({
      _k: t._k, landed_till_id: t.landed_till_id || null, meant_till_id: t.meant_till_id || null,
      amount: num(t.amount), reference: t.reference || "", note: t.note || "",
    })),
    account_receipts: (entry.account_receipts || []).map((a) => ({
      _k: a._k, account_id: a.account_id || null, method_id: a.method_id || null, till_id: a.till_id || null,
      amount: num(a.amount), reference: a.reference || "", note: a.note || "", bank: a.bank || null,
    })),
    invoice_lines: entry.invoice_lines.map((l) => ({
      _k: l._k, attendant_id: l.attendant_id || null, method_id: l.method_id || null, customer_id: l.customer_id || null,
      invoice_no: l.invoice_no || "", vehicle_reg: l.vehicle_reg || "", product_id: l.product_id || null,
      litres: num(l.litres), amount: num(l.amount),
    })),
    notes: entry.notes,
  });

  const save = async ({ silent = false } = {}) => {
    if (!silent) setSaving(true);
    try {
      const { data } = await api.put(`/shifts/${id}`, buildPayload());
      setShift(data);
      setDirty(false);
      setSaveError(null);
      if (!silent) toast.success("Draft saved");
    } catch (e) {
      // Autosave failures must never be silent — typed data would only live in this tab.
      const msg = e.response?.status === 401
        ? "Not saved: your login has expired. Sign in again in another tab, then come back and click Save Draft. Do not close this page."
        : `Not saved: ${formatApiErrorDetail(e.response?.data?.detail)}`;
      if (!silent || saveErrorRef.current !== msg) toast.error(msg, { duration: 15000 });
      setSaveError(msg);
    } finally {
      if (!silent) setSaving(false);
    }
  };

  saveRef.current = save;

  const runTransition = async () => {
    setBusy(true);
    try {
      const endpoints = {
        submit: `/shifts/${id}/submit`,
        "manager-review": `/shifts/${id}/manager-review`,
        "accountant-approve": `/shifts/${id}/accountant-approve`,
        lock: `/shifts/${id}/lock`,
        reopen: `/shifts/${id}/reopen`,
      };
      const body = transition.action === "reopen" ? { reason: note }
        : transition.action === "submit" ? { note, notify: emailOnSubmit } : { note };
      if (transition.action === "submit" && dirty) await api.put(`/shifts/${id}`, buildPayload());
      const { data } = await api.post(endpoints[transition.action], body);
      setShift(data);
      await load();
      setTransition(null);
      setNote("");
      setEmailOnSubmit(true);
      toast.success(`Shift ${transition.action.replace("-", " ")} done`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.validation_errors) {
        toast.error("Submission blocked: " + detail.validation_errors.join(" "));
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setBusy(false);
    }
  };

  const openAudit = async () => {
    const { data } = await api.get(`/shifts/${id}/audit`);
    setLogs(data);
    setAuditOpen(true);
  };

  const openAdjustments = async () => {
    const { data } = await api.get(`/shifts/${id}/adjustments`);
    setAdjustments(data);
    setAdjOpen(true);
  };

  const download = async (kind) => {
    try {
      const res = await api.get(`/reports/shift/${id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shift_${shift.date}_${shiftLabel(shift.shift_type, shift.date)}.${kind === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const t = computed.totals;
  const pumpById = Object.fromEntries(cfg.pumps.map((p) => [p.id, p]));
  const tankById = Object.fromEntries(cfg.tanks.map((tk) => [tk.id, tk]));
  const itemById = Object.fromEntries(cfg.nonfuel_items.map((i) => [i.id, i]));
  const methodById = Object.fromEntries(cfg.payment_methods.map((m) => [m.id, m]));
  const tillById = Object.fromEntries((cfg.tills || []).map((t) => [t.id, t]));
  const productById = Object.fromEntries(cfg.products.map((p) => [p.id, p]));
  const attendants = (cfg.attendants || []).filter((a) => a.active !== false);
  // Pumps without a KES sales register (Station Setup) hide the KES columns — amount = electronic liters × price
  const kesTotalizer = cfg.station?.pump_kes_totalizer !== false;
  // lubes also sold from a shop front (Kariokor): both places counted each shift
  const shopFront = !!cfg.station?.lube_shop_front;
  // spares sold straight from the store (Kariokor), paid in cash, M-Pesa, card or app - never on account
  const spareItems = cfg.nonfuel_items.filter((i) => ["spare", "service"].includes(i.category) && i.active !== false);
  const storeSaleMethods = cfg.payment_methods.filter((m) => m.active !== false && ["cash", "mobile_money", "card", "app"].includes(m.kind));

  // Section D shows a card for each attendant on this shift: added in Section D (Vivo expected),
  // assigned to a nozzle in Section A (automation down), or with collections entered — plus
  // Unassigned/Shop once it has data. It doesn't list every configured attendant up front.
  const assignedAttendantIds = new Set(Object.values(entry.pump_attendants || {}).filter(Boolean));
  Object.keys(entry.attendant_expected || {}).forEach((a) => assignedAttendantIds.add(a));
  Object.keys(entry.attendant_lube_sales || {}).forEach((a) => assignedAttendantIds.add(a));
  entry.payments.forEach((p) => { if (p.attendant_id && num(p.amount)) assignedAttendantIds.add(p.attendant_id); });
  const paymentGroups = [...attendants.filter((a) => assignedAttendantIds.has(a.id)), { id: "", name: "Unassigned / Shop" }];

  // group nozzles by pump, in nozzle-number order (N1, N2, … — the order of the shift sheet),
  // using natural sort so N10 comes after N9
  const islands = {};
  entry.fuel_readings.forEach((r, idx) => {
    const isl = pumpById[r.pump_id]?.island || "—";
    (islands[isl] = islands[isl] || []).push({ ...r, idx });
  });
  const byLabel = (a, b) => String(pumpById[a.pump_id]?.label || "").localeCompare(String(pumpById[b.pump_id]?.label || ""), undefined, { numeric: true });
  Object.values(islands).forEach((rows) => rows.sort(byLabel));
  // pumps in name order (Pump 1, Pump 2, Pump 3) — not the order their nozzles happen to be numbered
  const pumpGroups = Object.entries(islands).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));

  return (
    <div className="space-y-6 pb-28" onKeyDown={handleGridKeyDown}>
      {/* Sticky status banner */}
      <div className="sticky top-16 z-30 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-brand px-5 py-3 text-white shadow-md">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-slate-200 hover:bg-white/10 hover:text-white" data-testid="back-to-shifts">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="font-display text-lg font-bold leading-tight">
              {shift.date} · {shiftLabel(shift.shift_type, shift.date)} Shift
            </div>
            <div className="text-xs text-slate-300">Supervisor: {entry.supervisor_name || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {readOnly && <span className="flex items-center gap-1 text-xs text-amber-300"><Lock className="h-3.5 w-3.5" /> Direct edit disabled</span>}
          <StatusBadge status={shift.status} />
        </div>
      </div>

      {/* Variance summary (Section F) */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <SummaryStat label="Fuel Sales" value={`KSh ${kes(t.fuel_sales_total)}`} icon={Fuel} />
        <SummaryStat label="Non-Fuel Rev" value={`KSh ${kes(t.nonfuel_revenue_total)}`} icon={Package} />
        <SummaryStat label="Total Sales" value={`KSh ${kes(t.total_sales)}`} icon={Wallet} />
        <SummaryStat label="Expected Cash" value={`KSh ${kes(t.expected_cash)}`} icon={Receipt} />
        <SummaryStat label="Actual Cash" value={`KSh ${kes(t.actual_cash)}`} icon={Wallet} />
        <SummaryStat
          label="Cash Variance"
          value={`${t.cash_variance >= 0 ? "+" : ""}${kes(t.cash_variance)}`}
          icon={t.cash_status === "within" ? CheckCircle2 : TriangleAlert}
          status={t.cash_status}
        />
      </div>

      <Tabs defaultValue="pumps">
        <TabsList>
          <TabsTrigger value="pumps" data-testid="shift-tab-pumps"><Fuel className="mr-1.5 h-4 w-4" />Pumps</TabsTrigger>
          <TabsTrigger value="shop" data-testid="shift-tab-shop"><Package className="mr-1.5 h-4 w-4" />Lubes &amp; LPG</TabsTrigger>
          <TabsTrigger value="summary" data-testid="shift-tab-summary"><Wallet className="mr-1.5 h-4 w-4" />Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="pumps" className="mt-4 space-y-6">
      {/* Section A: Fuel pump readings */}
      <Section title="A · Fuel Pump Meter Readings" icon={Fuel}>
        <div className="mb-3 flex flex-wrap items-center gap-4 rounded-lg bg-slate-50 p-3">
          {cfg.products.map((p) => (
            <div key={p.id} className="flex items-center gap-2">
              <Label className="text-xs">{p.name} Price/L (KES)</Label>
              <span className="calc-cell inline-flex h-8 w-28 items-center justify-end rounded" data-testid={`fuel-price-${p.id}`}>
                {kes(entry.fuel_prices[p.id] || 0)}
              </span>
            </div>
          ))}
          <span className="text-xs text-muted-foreground">
            {shift.fuel_prices_locked
              ? "Prices fixed for this shift (e.g. a promo) — Station Setup price changes don't apply here."
              : "Set in Station Setup → Fuel Prices, not editable here."}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr>
                <th className="text-left" rowSpan={2}>Island / Pump</th>
                <th className="text-left" rowSpan={2}>Product</th>
                <th className="text-left" rowSpan={2} title="Only needed when the forecourt automation is down — otherwise attendants' expected sales come from Vivo (Section D)">Attendant<div className="text-[10px] font-normal normal-case text-muted-foreground">if automation down</div></th>
                <th colSpan={5} className="bg-slate-50">Manual Reading</th>
                <th colSpan={kesTotalizer ? 6 : 4} className="bg-blue-50">Electronic Reading (authoritative)</th>
                <th colSpan={2} className="bg-amber-50">Variance</th>
              </tr>
              <tr>
                <th className="bg-slate-50">Open (L)</th><th className="bg-slate-50">Close (L)</th><th className="bg-slate-50" title="Return to tank: litres through this nozzle that went back into the tank — not a sale">RTT (L)</th>
                <th className="bg-slate-50">Ltrs Sold</th><th className="bg-slate-50">Amount</th>
                <th className="bg-blue-50">Open (L)</th><th className="bg-blue-50">Close (L)</th><th className="bg-blue-50">Ltrs Sold</th>
                {kesTotalizer && <><th className="bg-blue-50">Open (KES)</th><th className="bg-blue-50">Close (KES)</th></>}
                <th className="bg-blue-50">Amount</th>
                <th className="bg-amber-50">Ltrs</th><th className="bg-amber-50">KES</th>
              </tr>
            </thead>
            <tbody>
              {pumpGroups.map(([isl, rows]) => (
                <Fragment key={isl}>
                  <tr><td colSpan={kesTotalizer ? 16 : 14} className="bg-slate-100 !text-left text-xs font-bold uppercase tracking-wider text-slate-600">{isl}</td></tr>
                  {rows.map((r) => {
                    const line = computed.fuel_lines.find((l) => l.pump_id === r.pump_id) || {};
                    return (
                      <tr key={r.pump_id}>
                        <td className="!text-left font-medium">{pumpById[r.pump_id]?.label}</td>
                        <td className="!text-left text-muted-foreground">{productById[r.product_id]?.name}</td>
                        <td className="!text-left">
                          <select
                            className="grid-input !text-left"
                            style={{ textAlign: "left" }}
                            disabled={readOnly}
                            value={entry.pump_attendants?.[r.pump_id] || ""}
                            data-testid={`pump-attendant-${r.pump_id}`}
                            onChange={(e) => set((n, v) => { n.pump_attendants = { ...(n.pump_attendants || {}) }; n.pump_attendants[r.pump_id] = v; })(e.target.value)}
                          >
                            <option value="">— assign —</option>
                            {attendants.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                          </select>
                        </td>
                        <td><NumCell value={r.opening} disabled={readOnly || r._openingLocked} tid={`pump-opening-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].opening = v))} /></td>
                        <td><NumCell value={r.closing} disabled={readOnly} tid={`pump-closing-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].closing = v))} /></td>
                        <td><NumCell value={r.testing} disabled={readOnly} tid={`pump-testing-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].testing = v))} /></td>
                        <td className="calc-cell">{ltr(line.manual_liters_sold)}</td>
                        <td className="calc-cell">{kes(line.manual_amount)}</td>
                        <td><NumCell value={r.elec_opening_l} disabled={readOnly || r._openingLocked} tid={`pump-elec-opening-l-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].elec_opening_l = v))} /></td>
                        <td><NumCell value={r.elec_closing_l} disabled={readOnly} tid={`pump-elec-closing-l-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].elec_closing_l = v))} /></td>
                        <td className="calc-cell">{ltr(line.electronic_liters_sold)}</td>
                        {kesTotalizer && <>
                          <td><NumCell value={r.elec_opening_kes} disabled={readOnly || r._openingLocked} tid={`pump-elec-opening-kes-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].elec_opening_kes = v))} /></td>
                          <td><NumCell value={r.elec_closing_kes} disabled={readOnly} tid={`pump-elec-closing-kes-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].elec_closing_kes = v))} /></td>
                        </>}
                        <td className="calc-cell font-semibold">{kes(line.electronic_amount)}</td>
                        <td className={`calc-cell ${Math.abs(line.variance_liters || 0) > 1 ? "text-rose-600" : ""}`}>{ltr(line.variance_liters)}</td>
                        <td className={`calc-cell ${Math.abs(line.variance_amount || 0) > 100 ? "text-rose-600" : ""}`}>{kes(line.variance_amount)}</td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
              <tr className="font-semibold">
                <td className="!text-left" colSpan={7}>Total Fuel Sales</td>
                <td className="calc-cell">{kes(computed.fuel_lines.reduce((a, l) => a + (l.manual_amount || 0), 0))}</td>
                <td className="calc-cell" colSpan={2}>—</td>
                <td className="calc-cell">{ltr(computed.fuel_lines.reduce((a, l) => a + (l.electronic_liters_sold || 0), 0))}</td>
                {kesTotalizer && <td className="calc-cell" colSpan={2}>—</td>}
                <td className="calc-cell">{kes(t.fuel_sales_total)}</td>
                <td className="calc-cell">{ltr(computed.fuel_lines.reduce((a, l) => a + (l.variance_liters || 0), 0))}</td>
                <td className="calc-cell">{kes(computed.fuel_lines.reduce((a, l) => a + (l.variance_amount || 0), 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* Section B: Tank dips */}
      <Section title="B · Tank Dip & Reconciliation" icon={Gauge}>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr>
                <th className="text-left" rowSpan={2}>Tank</th>
                <th rowSpan={2}>Deliveries</th><th rowSpan={2} title="Fuel put back into the tank that did NOT pass a nozzle meter">Other returns</th><th rowSpan={2} title="RTT entered on this tank's nozzles (Section A) — already left out of pump sales">RTT (from nozzles)</th><th rowSpan={2}>Pump Sales (L)</th>
                <th colSpan={6} className="bg-slate-50">Manual Dip</th>
                <th colSpan={6} className="bg-blue-50">TLS Gauge</th>
              </tr>
              <tr>
                <th className="bg-slate-50">Opening</th><th className="bg-slate-50">Closing</th><th className="bg-slate-50">Dip Sales (L)</th>
                <th className="bg-slate-50">Variance (L)</th><th className="bg-slate-50">Var %</th><th className="bg-slate-50 text-left">Status</th>
                <th className="bg-blue-50">Opening</th><th className="bg-blue-50">Closing</th><th className="bg-blue-50">Dip Sales (L)</th>
                <th className="bg-blue-50">Variance (L)</th><th className="bg-blue-50">Var %</th><th className="bg-blue-50 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {entry.tank_dips.map((r, idx) => {
                const line = computed.tank_lines.find((l) => l.tank_id === r.tank_id) || {};
                const hasItemizedDeliveries = entry.tank_deliveries.some((d) => d.tank_id === r.tank_id);
                return (
                  <tr key={r.tank_id}>
                    <td className="!text-left font-medium">{tankById[r.tank_id]?.label}</td>
                    <td>
                      {hasItemizedDeliveries ? (
                        <span className="calc-cell" title="From itemized deliveries below">{ltr(line.deliveries)}</span>
                      ) : (
                        <NumCell value={r.deliveries} disabled={readOnly} tid={`tank-deliveries-${r.tank_id}`} onChange={set((n, v) => (n.tank_dips[idx].deliveries = v))} />
                      )}
                    </td>
                    <td><NumCell value={r.rtt} disabled={readOnly} tid={`tank-rtt-${r.tank_id}`} onChange={set((n, v) => (n.tank_dips[idx].rtt = v))} /></td>
                    <td className="calc-cell text-muted-foreground" title="RTT entered on this tank's nozzles in Section A — back in the tank by the closing dip, and already left out of pump sales">{ltr(line.pump_test)}</td>
                    <td className="calc-cell">{ltr(line.pump_throughput)}</td>
                    <td><NumCell value={r.opening_dip} disabled={readOnly || r._openingLocked} tid={`tank-opening-${r.tank_id}`} onChange={set((n, v) => (n.tank_dips[idx].opening_dip = v))} /></td>
                    <td><NumCell value={r.closing_dip} disabled={readOnly} tid={`tank-closing-${r.tank_id}`} onChange={set((n, v) => (n.tank_dips[idx].closing_dip = v))} /></td>
                    <td className="calc-cell">{ltr(line.dip_sales)}</td>
                    <td className="calc-cell">{ltr(line.variance_l)}</td>
                    <td className="calc-cell">{ltr(line.variance_pct)}%</td>
                    <td className="!text-left"><VarianceBadge status={line.status} testId={`tank-variance-${r.tank_id}`}>{line.status}</VarianceBadge></td>
                    <td><NumCell value={r.tls_opening_dip} disabled={readOnly || r._tlsOpeningLocked} tid={`tank-tls-opening-${r.tank_id}`} onChange={set((n, v) => (n.tank_dips[idx].tls_opening_dip = v))} /></td>
                    <td><NumCell value={r.tls_closing_dip} disabled={readOnly} tid={`tank-tls-closing-${r.tank_id}`} onChange={set((n, v) => (n.tank_dips[idx].tls_closing_dip = v))} /></td>
                    <td className="calc-cell">{line.tls_dip_sales == null ? "—" : ltr(line.tls_dip_sales)}</td>
                    <td className="calc-cell">{line.tls_variance_l == null ? "—" : ltr(line.tls_variance_l)}</td>
                    <td className="calc-cell">{line.tls_variance_pct == null ? "—" : `${ltr(line.tls_variance_pct)}%`}</td>
                    <td className="!text-left">{line.tls_status ? <VarianceBadge status={line.tls_status} testId={`tank-tls-variance-${r.tank_id}`}>{line.tls_status}</VarianceBadge> : <span className="text-xs text-muted-foreground">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Section B2: Fuel deliveries (itemized, feeds Tank Dip "Deliveries") */}
      <Section title="Fuel Deliveries" icon={Truck}>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr>
                <th className="text-left">Tank</th>
                <th className="text-left">Invoice No.</th>
                <th className="text-left">Date</th>
                <th className="text-left">Product</th>
                <th>Invoice Amount (KES)</th>
                <th>Invoice Volume (L)</th>
                <th>Offloaded Qty (L)</th>
                <th>Variance (L)</th>
                <th>Var %</th>
                <th className="text-left">Status</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {entry.tank_deliveries.map((d, idx) => {
                const line = computed.tank_delivery_lines?.[idx] || {};
                return (
                  <tr key={d._k}>
                    <td className="!text-left">
                      <select
                        className="grid-input !text-left"
                        style={{ textAlign: "left" }}
                        disabled={readOnly}
                        value={d.tank_id || ""}
                        data-testid={`delivery-tank-${idx}`}
                        onChange={(e) => set((n, v) => {
                          n.tank_deliveries[idx].tank_id = v;
                          n.tank_deliveries[idx].product_id = tankById[v]?.product_id || "";
                        })(e.target.value)}
                      >
                        <option value="">— select tank —</option>
                        {cfg.tanks.map((tk) => <option key={tk.id} value={tk.id}>{tk.label}</option>)}
                      </select>
                    </td>
                    <td className="!text-left">
                      <input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={d.invoice_no || ""} data-testid={`delivery-invoice-${idx}`} onChange={(e) => set((n, v) => (n.tank_deliveries[idx].invoice_no = v))(e.target.value)} />
                    </td>
                    <td className="!text-left">
                      <input type="date" className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={d.date || ""} data-testid={`delivery-date-${idx}`} onChange={(e) => set((n, v) => (n.tank_deliveries[idx].date = v))(e.target.value)} />
                    </td>
                    <td className="!text-left text-muted-foreground">{productById[d.product_id]?.name || productById[tankById[d.tank_id]?.product_id]?.name || "—"}</td>
                    <td><NumCell value={d.invoice_amount} disabled={readOnly} tid={`delivery-amount-${idx}`} onChange={set((n, v) => (n.tank_deliveries[idx].invoice_amount = v))} /></td>
                    <td><NumCell value={d.invoice_volume} disabled={readOnly} tid={`delivery-invoice-vol-${idx}`} onChange={set((n, v) => (n.tank_deliveries[idx].invoice_volume = v))} /></td>
                    <td><NumCell value={d.offloaded_quantity} disabled={readOnly} tid={`delivery-offloaded-${idx}`} onChange={set((n, v) => (n.tank_deliveries[idx].offloaded_quantity = v))} /></td>
                    <td className={`calc-cell ${Math.abs(line.variance || 0) > 1 ? "text-rose-600" : ""}`}>{ltr(line.variance)}</td>
                    <td className="calc-cell">{ltr(line.variance_pct)}%</td>
                    <td className="!text-left"><VarianceBadge status={line.status} testId={`delivery-status-${idx}`}>{line.status}</VarianceBadge></td>
                    <td className="text-center">{!readOnly && <button onClick={() => set((n) => n.tank_deliveries.splice(idx, 1))(null)} className="text-rose-500" data-testid={`delivery-remove-${idx}`}>×</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!readOnly && (
          <Button
            variant="outline" size="sm" className="mt-3"
            onClick={() => set((n) => n.tank_deliveries.push({ tank_id: "", invoice_no: "", date: "", product_id: "", invoice_amount: "", invoice_volume: "", offloaded_quantity: "", _k: uid() }))(null)}
            data-testid="add-delivery-button"
          >
            + Add Delivery
          </Button>
        )}
      </Section>
        </TabsContent>

        <TabsContent value="shop" className="mt-4 space-y-6">
      {/* Lubes & LPG: value sold per the stock count (Section C below) vs what everyone declared
          collecting — by person and payment method. The money joins the shift totals by method:
          cash into cash received / banking, M-Pesa into the till, invoices into credit. */}
      <Section title="Lubes & LPG Reconciliation" icon={ClipboardCheck}>
        {(() => {
          const groups = computed.nonfuel_group_lines;
          const label = { lube: "Lubes", lpg_accessory: "LPG & Accessories" };
          const collected = (g) => (g.attendant_collected || 0) + (g.declared_amount || 0);
          const sum = (f) => groups.reduce((acc, g) => acc + f(g), 0);
          const rows = [...groups.map((g) => ({ name: label[g.group], sold: g.sold_revenue, got: collected(g) })),
            { name: "Total", sold: sum((g) => g.sold_revenue), got: sum(collected), total: true }];
          const legacy = Object.entries(entry.attendant_lube_sales || {}).filter(([, v]) => num(v));
          return (
            <div className="space-y-2">
              <div className="overflow-x-auto">
                <table className="recon-table">
                  <thead><tr><th className="text-left" /><th>Sold (stock count × price)</th><th>Declared (collections below)</th><th>Variance</th></tr></thead>
                  <tbody>
                    {rows.map((rw) => {
                      const v = rw.got - rw.sold;
                      return (
                        <tr key={rw.name} className={rw.total ? "font-semibold" : ""} data-testid={`nf-recon-${rw.name}`}>
                          <td className="!text-left">{rw.name}</td>
                          <td className="calc-cell">{kes(rw.sold)}</td>
                          <td className="calc-cell">{kes(rw.got)}</td>
                          <td className={`calc-cell ${Math.abs(v) > 0.5 ? (v < 0 ? "text-rose-600" : "text-amber-600") : "text-emerald-600"}`}>{v >= 0 ? "+" : ""}{kes(v)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">A negative variance means stock left the shelf without being paid for.</p>
              {legacy.length > 0 && (
                <p className="text-xs text-amber-700">
                  Also counted (older entry in the attendants&apos; Lubes box): {legacy.map(([id, v]) => `${attendants.find((x) => x.id === id)?.name || "?"} ${kes(num(v), 0)}`).join(" · ")}
                </p>
              )}
            </div>
          );
        })()}
      </Section>

      <Section title="Lubes & LPG Collections" icon={Wallet}>
        <p className="mb-2 text-xs text-muted-foreground">One row per person and way of payment: cash, M-Pesa (with its till), Visa, Shell Card or an invoice customer (e.g. the garage) — invoices are then itemized to the customer under Invoices &amp; short-term debtors.</p>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead><tr><th className="text-left">Collected by</th><th className="text-left">For</th><th className="text-left">Paid by</th><th>Amount (KES)</th><th className="text-left">Reference</th><th className="w-10" /></tr></thead>
            <tbody>
              {entry.nonfuel_payments.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-sm text-muted-foreground">No lube or LPG money recorded yet — add a row per person.</td></tr>
              )}
              {entry.nonfuel_payments.map((p, idx) => (
                <tr key={p._k} data-testid={`nf-collect-${idx}`}>
                  <td className="!text-left">
                    <select className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={p.attendant_id || ""}
                      data-testid={`nf-collect-who-${idx}`} onChange={(e) => set((n, v) => (n.nonfuel_payments[idx].attendant_id = v))(e.target.value)}>
                      <option value="">— who? —</option>
                      {attendants.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </td>
                  <td className="!text-left">
                    <select className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={p.group}
                      data-testid={`nf-collect-group-${idx}`} onChange={(e) => set((n, v) => (n.nonfuel_payments[idx].group = v))(e.target.value)}>
                      <option value="lube">Lubes</option>
                      <option value="lpg_accessory">LPG &amp; Accessories</option>
                    </select>
                  </td>
                  <td className="!text-left">
                    <select className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={p.method_id || ""}
                      data-testid={`nf-collect-method-${idx}`}
                      onChange={(e) => set((n, v) => { n.nonfuel_payments[idx].method_id = v; if (methodById[v]?.kind !== "mobile_money") n.nonfuel_payments[idx].till_id = ""; })(e.target.value)}>
                      {cfg.payment_methods.filter((m) => m.active !== false && !["payout", "unpaid"].includes(m.kind)).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                    {methodById[p.method_id]?.kind === "mobile_money" && (
                      <select className="grid-input mt-1 !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={p.till_id || ""}
                        onChange={(e) => set((n, v) => (n.nonfuel_payments[idx].till_id = v))(e.target.value)}>
                        <option value="">— till —</option>
                        {(cfg.tills || []).filter((x) => x.active !== false).map((x) => <option key={x.id} value={x.id}>{x.label || x.till_number}</option>)}
                      </select>
                    )}
                  </td>
                  <td><NumCell value={p.amount} disabled={readOnly} tid={`nf-collect-amount-${idx}`} onChange={set((n, v) => (n.nonfuel_payments[idx].amount = v))} /></td>
                  <td className="!text-left"><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={p.reference || ""}
                    data-testid={`nf-collect-ref-${idx}`} onChange={(e) => set((n, v) => (n.nonfuel_payments[idx].reference = v))(e.target.value)} /></td>
                  <td className="text-center">{!readOnly && <button onClick={() => set((n) => n.nonfuel_payments.splice(idx, 1))(null)} className="text-rose-500" data-testid={`nf-collect-remove-${idx}`}>×</button>}</td>
                </tr>
              ))}
              {entry.nonfuel_payments.length > 0 && (() => {
                const byPerson = {};
                computed.nonfuel_payment_lines.forEach((l) => { const k = l.attendant_id || ""; byPerson[k] = (byPerson[k] || 0) + l.amount; });
                return (
                  <tr className="font-semibold">
                    <td className="!text-left" colSpan={2}>
                      Total declared
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {Object.entries(byPerson).map(([id, v]) => `${attendants.find((x) => x.id === id)?.name || "—"} ${kes(v, 0)}`).join(" · ")}
                      </span>
                    </td>
                    <td />
                    <td className="calc-cell">{kes(computed.nonfuel_payment_lines.reduce((acc, l) => acc + l.amount, 0))}</td>
                    <td colSpan={2} />
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
        {!readOnly && (
          <Button variant="outline" size="sm" className="mt-3" data-testid="add-nf-collection"
            onClick={() => set((n) => n.nonfuel_payments.push({ _k: uid(), attendant_id: "", group: "lube", method_id: cfg.payment_methods.find((m) => m.kind === "cash")?.id || "", amount: "", reference: "", till_id: "" }))(null)}>
            + Add collection
          </Button>
        )}
      </Section>

      {/* Section C: Non-fuel */}
      <Section title="C · Non-Fuel Stock (Lubes, LPG, Accessories)" icon={Package}>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr>
                <th className="text-left">Item</th>
                {shopFront ? <><th>Opening forecourt</th><th>Opening shop</th></> : <th>Opening</th>}
                <th title="Moved from the store onto the forecourt this shift — taken off the store stock (Store page)">Added from store</th><th title="LPG cylinders returned into stock">Returned</th>
                {shopFront ? <><th>Closing forecourt</th><th>Closing shop</th></> : <th>Closing</th>}
                <th title={shopFront ? "Forecourt and shop together: opening + added + returned − closing" : undefined}>Sold</th>
                <th>Unit Price</th><th>Revenue</th>
                {isAccountant && <><th>Cost Price</th><th>Gross Profit</th></>}
              </tr>
            </thead>
            <tbody>
              {entry.nonfuel.map((r, idx) => {
                const line = computed.nonfuel_lines.find((l) => l.item_id === r.item_id) || {};
                const item = itemById[r.item_id];
                const hasItemizedDeliveries = entry.nonfuel_deliveries.some((d) => d.item_id === r.item_id);
                return (
                  <tr key={r.item_id}>
                    <td className="!text-left font-medium">{item?.name} <span className="text-xs text-muted-foreground">({item?.category})</span></td>
                    <td><NumCell value={r.opening} disabled={readOnly || r._openingLocked} tid={`nf-opening-${r.item_id}`} onChange={set((n, v) => (n.nonfuel[idx].opening = v))} /></td>
                    {shopFront && <td>{item?.category === "lube"
                      ? <NumCell value={r.shop_opening} disabled={readOnly || r._shopOpeningLocked} tid={`nf-shop-opening-${r.item_id}`} onChange={set((n, v) => (n.nonfuel[idx].shop_opening = v))} />
                      : <span className="text-xs text-muted-foreground">—</span>}</td>}
                    <td>
                      {hasItemizedDeliveries ? (
                        <span className="calc-cell" title="From itemized deliveries below">{ltr(line.received, 0)}</span>
                      ) : (
                        <NumCell value={r.received} disabled={readOnly} tid={`nf-received-${r.item_id}`} onChange={set((n, v) => (n.nonfuel[idx].received = v))} />
                      )}
                    </td>
                    <td>
                      {item?.category === "lpg" ? (
                        <NumCell value={r.returned} disabled={readOnly} tid={`nf-returned-${r.item_id}`} onChange={set((n, v) => (n.nonfuel[idx].returned = v))} />
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td><NumCell value={r.closing} disabled={readOnly} tid={`nf-closing-${r.item_id}`} onChange={set((n, v) => (n.nonfuel[idx].closing = v))} /></td>
                    {shopFront && <td>{item?.category === "lube"
                      ? <NumCell value={r.shop_closing} disabled={readOnly} tid={`nf-shop-closing-${r.item_id}`} onChange={set((n, v) => (n.nonfuel[idx].shop_closing = v))} />
                      : <span className="text-xs text-muted-foreground">—</span>}</td>}
                    <td className={`calc-cell ${line.sold < 0 ? "!bg-rose-50 font-semibold text-rose-600" : ""}`}
                      title={line.sold < 0 ? "Count went up with nothing added from the store — enter what came from the store" : undefined}>{ltr(line.sold, 0)}</td>
                    <td className="calc-cell" title="Set in Station Setup → Items" data-testid={`nf-price-${r.item_id}`}>{kes(r.unit_price)}</td>
                    <td className="calc-cell">{kes(line.revenue)}</td>
                    {isAccountant && <>
                      <td className="calc-cell">{kes(line.cost_price)}</td>
                      <td className="calc-cell">{kes(line.gross_profit)}</td>
                    </>}
                  </tr>
                );
              })}
              <tr className="font-semibold">
                <td className="!text-left" colSpan={shopFront ? 9 : 7}>Non-Fuel Totals</td>
                <td className="calc-cell">{kes(t.nonfuel_revenue_total)}</td>
                {isAccountant && <td className="calc-cell" colSpan={2}>{kes(t.nonfuel_gross_total)}</td>}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {cfg.station?.spares_store && (
        // Spares (filters, air cleaners) sold straight from the store: never counted per shift, each sale
        // written down at the Station Setup price; the month-end store count catches one that wasn't.
        <Section title="Store sales — filters, air cleaners & services" icon={Package}>
          <p className="mb-3 text-xs text-muted-foreground">
            One line per sale. The price is the one in Station Setup. The money is part of this shift (cash, M-Pesa into the till,
            card) — so don&apos;t declare it again under the attendants. The store&apos;s stock goes down by what is sold here;
            the month-end store count shows anything sold but not written down.
          </p>
          <table className="recon-table">
            <thead><tr><th className="text-left">Item</th><th>Qty</th><th>Price</th><th>Amount</th><th className="text-left">Paid by</th><th className="text-left">Till</th><th className="text-left">Sold by</th><th className="text-left">Receipt / car reg.</th><th className="w-8" /></tr></thead>
            <tbody>
              {(entry.store_sales || []).length === 0 && <tr><td colSpan={9} className="py-3 text-center text-sm text-muted-foreground">No store sales on this shift.</td></tr>}
              {(entry.store_sales || []).map((row, idx) => {
                const it = itemById[row.item_id];
                const service = it?.category === "service";  // e.g. greasing: the price is entered on the sale
                const price = it ? (service ? num(row.unit_price) : Number(it.unit_price || 0)) : 0;
                const mm = methodById[row.method_id]?.kind === "mobile_money";
                const upd = (field) => (e) => set((n, v) => { n.store_sales[idx][field] = v; if (field === "method_id" && methodById[v]?.kind !== "mobile_money") n.store_sales[idx].till_id = ""; })(e.target.value);
                return (
                  <tr key={row._k}>
                    <td className="!text-left">
                      <select className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={row.item_id || ""} onChange={upd("item_id")} data-testid={`ss-item-${idx}`}>
                        <option value="">— select —</option>
                        {spareItems.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                      </select>
                    </td>
                    <td><input type="number" className="grid-input" disabled={readOnly} value={row.qty} onChange={upd("qty")} data-testid={`ss-qty-${idx}`} /></td>
                    {service ? (
                      <td><input type="number" className="grid-input" disabled={readOnly} value={row.unit_price ?? ""} placeholder="Price"
                        onChange={upd("unit_price")} data-testid={`ss-price-${idx}`} /></td>
                    ) : <td className="calc-cell">{it ? kes(price) : ""}</td>}
                    <td className="calc-cell">{it ? kes(price * num(row.qty)) : ""}</td>
                    <td className="!text-left">
                      <select className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={row.method_id || ""} onChange={upd("method_id")} data-testid={`ss-method-${idx}`}>
                        <option value="">— select —</option>
                        {storeSaleMethods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </td>
                    <td className="!text-left">
                      {mm ? (
                        <select className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={row.till_id || ""} onChange={upd("till_id")}>
                          <option value="">— till —</option>
                          {(cfg.tills || []).filter((x) => x.active !== false).map((x) => <option key={x.id} value={x.id}>{x.label || x.till_number}</option>)}
                        </select>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="!text-left">
                      <select className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={row.attendant_id || ""} onChange={upd("attendant_id")}>
                        <option value="">— who —</option>
                        {attendants.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </td>
                    <td className="!text-left"><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={row.reference || ""} onChange={upd("reference")} /></td>
                    <td className="text-center">{!readOnly && <button className="text-rose-500" onClick={() => set((n) => n.store_sales.splice(idx, 1))(null)}>×</button>}</td>
                  </tr>
                );
              })}
              {(entry.store_sales || []).length > 0 && (
                <tr className="font-semibold"><td className="!text-left" colSpan={3}>Store sales total</td><td className="calc-cell">{kes(t.store_sales_total || 0)}</td><td colSpan={5} /></tr>
              )}
            </tbody>
          </table>
          {!readOnly && (
            <Button variant="outline" size="sm" className="mt-3" data-testid="ss-add"
              onClick={() => set((n) => { n.store_sales = n.store_sales || []; n.store_sales.push({ _k: uid(), item_id: "", qty: "", method_id: storeSaleMethods.find((m) => m.kind === "cash")?.id || "", till_id: "", attendant_id: "", reference: "" }); })(null)}>
              <Plus className="mr-1 h-4 w-4" />Add a sale
            </Button>
          )}
          {spareItems.length === 0 && <p className="mt-2 text-xs text-rose-700">No spares set up yet — add the filters and air cleaners in Station Setup → Items with category &ldquo;Spare&rdquo;.</p>}
        </Section>
      )}

      <p className="text-xs text-muted-foreground">
        Supplier deliveries of lubes &amp; LPG are recorded in the <a href="/store" className="underline">Store</a>; here, &ldquo;Added from store&rdquo; is what was moved onto the forecourt this shift.
      </p>

        </TabsContent>

        <TabsContent value="summary" className="mt-4 space-y-6">
      {/* Section D: Payments, entered per attendant — only shows attendants actually assigned to a
          pump this shift (Section A), plus Unassigned/Shop once it has data */}
      <Section title="D · Attendant Payment Collections" icon={Wallet}>
        <div className="space-y-4">
          {!readOnly && (
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="grid-input !w-auto !text-left" style={{ textAlign: "left" }} value=""
                data-testid="add-shift-attendant"
                onChange={(e) => e.target.value && set((n, v) => { n.attendant_expected = { ...(n.attendant_expected || {}), [v]: "" }; })(e.target.value)}
              >
                <option value="">+ Add attendant on this shift…</option>
                {attendants.filter((a) => !assignedAttendantIds.has(a.id)).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <span className="text-xs text-muted-foreground">
                {shift?.vivo_includes_nonfuel
                  ? "Enter each attendant's expected exactly as Vivo shows it (it includes the lubes & LPG they sold) — the app takes off their lube/LPG money for the fuel part."
                  : "Enter each attendant's expected fuel sales from the Vivo system."} Only assign nozzles in Section A if the automation is down.
              </span>
            </div>
          )}
          {assignedAttendantIds.size === 0 && !entry.payments.some((p) => !p.attendant_id && num(p.amount)) && (
            <p className="text-sm text-muted-foreground" data-testid="payments-empty-hint">
              No attendants on this shift yet.
            </p>
          )}
          {paymentGroups.map((att) => {
            const rows = entry.payments.map((r, idx) => ({ ...r, idx })).filter((r) => (r.attendant_id || "") === att.id);
            const line = computed.attendant_lines.find((l) => (l.attendant_id || "") === (att.id || "")) || { assigned_fuel_sales: 0, collected_total: 0, variance: 0 };
            if (att.id === "" && rows.every((r) => !num(r.amount)) && !line.assigned_fuel_sales) return null;
            return (
              <Card key={att.id || "unassigned"} className="overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-slate-50 px-4 py-2.5">
                  <h4 className="text-sm font-semibold text-slate-800">{att.name}</h4>
                  {att.id && (
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <label className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Vivo expected</span>
                        <span className="w-28"><NumCell value={entry.attendant_expected?.[att.id] ?? ""} disabled={readOnly} tid={`att-expected-${att.id}`}
                          onChange={set((n, v) => { n.attendant_expected = { ...(n.attendant_expected || {}), [att.id]: v }; })} /></span>
                      </label>
                      {num(entry.attendant_lube_sales?.[att.id]) > 0 && (
                        <span className="text-muted-foreground" title="Older entry — lube money is now recorded under Lubes & LPG Collections">
                          Lubes (old entry): {kes(num(entry.attendant_lube_sales[att.id]), 0)}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                    <span>
                      Expected: <span className="font-mono-fin font-semibold text-slate-700">KSh {kes(line.expected_total ?? line.assigned_fuel_sales)}</span>
                      {line.expected_source === "pumps" && att.id && <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800" title="No Vivo figure entered — using meter sales of the nozzles assigned in Section A">from nozzles</span>}
                    </span>
                    <span>Collected: <span className="font-mono-fin font-semibold text-slate-700">KSh {kes(line.collected_total)}</span></span>
                    <span className={`font-mono-fin font-semibold ${Math.abs(line.variance || 0) <= 100 ? "text-emerald-600" : "text-rose-600"}`}>
                      Variance: {line.variance >= 0 ? "+" : ""}{kes(line.variance)}
                    </span>
                    {line.status && <VarianceBadge status={line.status}>{line.status}</VarianceBadge>}
                  </div>
                </div>
                <div className="overflow-x-auto p-3">
                  <table className="recon-table">
                    <thead><tr><th className="text-left">Payment Method</th><th className="text-left">Type</th><th>Amount (KES)</th><th className="text-left">Reference</th></tr></thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.method_id}>
                          <td className="!text-left font-medium">{methodById[r.method_id]?.name}</td>
                          <td className="!text-left text-xs text-muted-foreground">{methodById[r.method_id]?.kind}</td>
                          <td><NumCell value={r.amount} disabled={readOnly} tid={`pay-amount-${att.id || "unassigned"}-${r.method_id}`} onChange={set((n, v) => (n.payments[r.idx].amount = v))} /></td>
                          <td><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={r.reference} data-testid={`pay-ref-${att.id || "unassigned"}-${r.method_id}`} onChange={(e) => set((n, v) => (n.payments[r.idx].reference = v))(e.target.value)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })}
        </div>
      </Section>

      {/* Section D2: Shift-wide payment totals + cash reconciliation (derived from attendant entries above) */}
      <Section title="Payment Totals & Cash Reconciliation" icon={Receipt}>
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="overflow-x-auto">
            <table className="recon-table">
              <thead><tr><th className="text-left">Payment Method</th><th className="text-left">Type</th><th>Shift Total (KES)</th></tr></thead>
              <tbody>
                {cfg.payment_methods.filter((m) => m.active !== false).map((m) => (
                  <tr key={m.id}>
                    <td className="!text-left font-medium">{m.name}</td>
                    <td className="!text-left text-xs text-muted-foreground">{m.kind}</td>
                    <td className="calc-cell">{kes(t.payments_by_method?.[m.id] || 0)}</td>
                  </tr>
                ))}
                <tr className="font-semibold"><td className="!text-left" colSpan={2}>Payments Total</td><td className="calc-cell">{kes(t.payments_total)}</td></tr>
              </tbody>
            </table>
          </div>
          <Card className="p-5">
            <h4 className="font-display font-semibold text-slate-800">Cash Reconciliation</h4>
            <dl className="mt-4 space-y-2 text-sm">
              {t.system_expected_total != null && (
                <div className="mb-3 space-y-2 rounded-lg bg-slate-50 p-3">
                  <Row label="Vivo-reported fuel sales (attendants)" value={kes(t.system_expected_total)} />
                  <Row label="Fuel sales from meter readings" value={kes(t.fuel_sales_total)} />
                  <div className={Math.abs(t.system_vs_meter_variance) > 100 ? "text-rose-600" : ""}>
                    <Row label="Vivo − meters" value={`${t.system_vs_meter_variance >= 0 ? "+" : ""}${kes(t.system_vs_meter_variance)}`} bold />
                  {t.vivo_check && (
                    <div className="mt-2 rounded border border-sky-200 bg-sky-50 px-2 py-1.5" data-testid="vivo-check">
                      <div className="text-xs font-semibold text-sky-900">Vivo check (against stock-counted lubes &amp; LPG)</div>
                      <Row label="Vivo expected, all attendants (incl. lubes & LPG)" value={kes(t.vivo_check.vivo_total)} />
                      <Row label="Less: lubes & LPG sold (stock counts)" value={`(${kes(t.vivo_check.nonfuel_sold)})`} />
                      <Row label="= Vivo fuel" value={kes(t.vivo_check.vivo_fuel)} />
                      <Row label="Fuel sales (meters)" value={kes(t.vivo_check.meter_fuel)} />
                      <Row label="Difference" value={`${t.vivo_check.difference >= 0 ? "+" : ""}${kes(t.vivo_check.difference)}`} bold />
                    </div>
                  )}
                  </div>
                </div>
              )}
              <Row label={t.expected_cash_basis === "vivo" ? "Fuel sales expected (Vivo, by attendant)" : "Fuel sales (meter readings)"} value={kes(t.fuel_expected_basis)} />
              <Row label="Lubes & LPG sales" value={kes(t.nonfuel_revenue_total)} />
              {t.rtt_liters_total > 0 && (
                <div className="rounded bg-sky-50 px-2 py-1 text-xs text-sky-900" data-testid="rtt-summary">
                  Returned to tank (RTT): {ltr(t.rtt_liters_total)} L = KSh {kes(t.rtt_value_total)} — not a sale, excluded above
                </div>
              )}
              <Row label="Less: Non-cash collections" value={`(${kes(t.non_cash_total)})`} />
              <Row label="Less: Expenses" value={`(${kes(t.expenses_total)})`} />
              <div className="border-t pt-2"><Row label="Expected Cash" value={kes(t.expected_cash)} bold /></div>
              <div className="border-t pt-2"><Row label="Cash Declared (sum of attendant Cash entries)" value={kes(t.actual_cash)} bold /></div>
              <div className={`mt-2 flex items-center justify-between rounded-lg px-3 py-2 ${t.cash_status === "within" ? "var-within" : t.cash_status === "warning" ? "var-warning" : "var-critical"}`}>
                <span className="text-sm font-medium">Cash Variance</span>
                <span className="font-mono-fin font-bold" data-testid="cash-variance-display">{t.cash_variance >= 0 ? "+" : ""}{kes(t.cash_variance)}</span>
              </div>
              {/* Thome declares LPG cash on its own: the cash above split into LPG and the rest */}
              {cfg.station?.lpg_cash_separate && (() => {
                const lpgSold = computed.nonfuel_group_lines.find((g) => g.group === "lpg_accessory")?.sold_revenue || 0;
                const lpgCash = computed.nonfuel_payment_lines.filter((l) => l.group === "lpg_accessory" && l.kind === "cash").reduce((a, l) => a + l.amount, 0);
                const rest = { expected: t.expected_cash - lpgSold, declared: t.actual_cash - lpgCash };
                const sign = (v) => `${v >= 0 ? "+" : ""}${kes(v)}`;
                const tone = (v) => (Math.abs(v) < 0.5 ? "text-emerald-700" : Math.abs(v) <= 100 ? "text-amber-700" : "text-rose-600");
                return (
                  <div className="mt-2 space-y-2 rounded-lg border p-3" data-testid="lpg-cash-split">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cash, by what it&apos;s for</div>
                    <div>
                      <div className="text-sm font-medium text-slate-800">Fuel &amp; lubes cash</div>
                      <Row label="Expected" value={kes(rest.expected)} />
                      <Row label="Declared" value={kes(rest.declared)} />
                      <div className={tone(rest.declared - rest.expected)}><Row label="Over / short" value={sign(rest.declared - rest.expected)} bold /></div>
                    </div>
                    <div className="border-t pt-2">
                      <div className="text-sm font-medium text-slate-800">LPG cash</div>
                      <Row label="LPG sold (stock count)" value={kes(lpgSold)} />
                      <Row label="LPG cash declared" value={kes(lpgCash)} />
                      <div className={tone(lpgCash - lpgSold)}><Row label="Over / short" value={sign(lpgCash - lpgSold)} bold /></div>
                    </div>
                  </div>
                );
              })()}
              {t.topups_total > 0 && (
                <div className="border-t pt-2"><Row label="Shell Card top-ups received (owed to Vivo)" value={kes(t.topups_total)} /></div>
              )}
              {t.account_cash_received > 0 && (
                <div className="border-t pt-2">
                  <Row label="Add: customers' account payments in cash" value={kes(t.account_cash_received)} />
                  <Row label="Cash to bank" value={kes(t.cash_to_bank)} bold />
                </div>
              )}
            </dl>
          </Card>
        </div>
      </Section>

      {/* Credit sales itemized per invoice — each attendant's Invoice / Montrose total broken into
          customer invoices (customer, invoice/LPO no., vehicle, product, litres). Feeds the
          debtors ledger (Accounting → Debtors & Settlements). Montrose lines are optional details. */}
      {(computed.invoice_checks.length > 0 || entry.invoice_lines.length > 0) && (
      <Section title="Invoices (credit sales)" icon={FileText}>
        {(() => {
          const creditMethods = cfg.payment_methods.filter((m) => m.kind === "invoice" && m.active !== false);
          const linkedTo = Object.fromEntries((cfg.receivable_accounts || []).filter((a) => a.method_id).map((a) => [a.method_id, a]));
          const customers = (cfg.receivable_accounts || []).filter((a) => a.kind === "customer" && a.active !== false && !a.method_id);
          return (
            <>
              <div className="mb-3 flex flex-wrap gap-2" data-testid="invoice-checks">
                {computed.invoice_checks.map((c) => {
                  const ok = Math.abs(c.difference) < 0.5;
                  return (
                    <span key={`${c.attendant_id}|${c.method_id}`}
                      className={`rounded-lg border px-3 py-1.5 text-xs ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : c.optional ? "border-slate-200 bg-slate-50 text-slate-700" : "border-amber-300 bg-amber-50 text-amber-900"}`}>
                      <span className="font-semibold">{c.attendant_name}</span> · {c.method_name}: {kes(c.declared)} · itemized {kes(c.itemized)}
                      {ok ? " ✓" : c.difference > 0 ? ` · ${kes(c.difference)} ${c.optional ? "not detailed (optional)" : "still to itemize"}` : ` · itemized ${kes(-c.difference)} MORE than declared`}
                    </span>
                  );
                })}
              </div>
              <div className="overflow-x-auto">
                <table className="recon-table">
                  <thead><tr>
                    <th className="text-left">Attendant</th><th className="text-left">Column</th><th className="text-left">Customer</th>
                    <th className="text-left">Invoice / LPO no.</th><th className="text-left">Vehicle reg</th><th className="text-left">Product</th>
                    <th>Litres</th><th>Amount (KES)</th><th className="w-10" />
                  </tr></thead>
                  <tbody>
                    {entry.invoice_lines.length === 0 && (
                      <tr><td colSpan={9} className="py-4 text-center text-sm text-muted-foreground">No invoices itemized yet — add one line per invoice.</td></tr>
                    )}
                    {entry.invoice_lines.map((ln, idx) => {
                      const linked = linkedTo[ln.method_id];
                      const setLn = (field) => (v) => set((n) => { n.invoice_lines[idx][field] = v; })(v);
                      const fillAmount = (litres, productId) => {
                        const price = num(entry.fuel_prices?.[productId]);
                        if (num(litres) && price) set((n) => { n.invoice_lines[idx].amount = String(Math.round(num(litres) * price * 100) / 100); })(null);
                      };
                      return (
                        <tr key={ln._k} data-testid={`inv-line-${idx}`}>
                          <td className="!text-left">
                            <select className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={ln.attendant_id || ""}
                              data-testid={`inv-att-${idx}`} onChange={(e) => setLn("attendant_id")(e.target.value)}>
                              <option value="">— who? —</option>
                              {attendants.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                          </td>
                          <td className="!text-left">
                            <select className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={ln.method_id || ""}
                              data-testid={`inv-method-${idx}`} onChange={(e) => setLn("method_id")(e.target.value)}>
                              {creditMethods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                          </td>
                          <td className="!text-left">
                            {linked ? <span className="text-xs text-muted-foreground">{linked.name}</span> : (
                              <select className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={ln.customer_id || ""}
                                data-testid={`inv-customer-${idx}`} onChange={(e) => setLn("customer_id")(e.target.value)}>
                                <option value="">— customer —</option>
                                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                              </select>
                            )}
                          </td>
                          <td className="!text-left"><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={ln.invoice_no || ""}
                            data-testid={`inv-no-${idx}`} onChange={(e) => setLn("invoice_no")(e.target.value)} /></td>
                          <td className="!text-left"><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={ln.vehicle_reg || ""}
                            data-testid={`inv-vehicle-${idx}`} onChange={(e) => setLn("vehicle_reg")(e.target.value.toUpperCase())} /></td>
                          <td className="!text-left">
                            <select className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={ln.product_id || ""}
                              data-testid={`inv-product-${idx}`} onChange={(e) => { setLn("product_id")(e.target.value); fillAmount(ln.litres, e.target.value); }}>
                              <option value="">—</option>
                              {cfg.products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                          </td>
                          <td><NumCell value={ln.litres ?? ""} disabled={readOnly} tid={`inv-litres-${idx}`}
                            onChange={(v) => { setLn("litres")(v); fillAmount(v, ln.product_id); }} /></td>
                          <td><NumCell value={ln.amount ?? ""} disabled={readOnly} tid={`inv-amount-${idx}`} onChange={setLn("amount")} /></td>
                          <td className="text-center">{!readOnly && <button onClick={() => set((n) => n.invoice_lines.splice(idx, 1))(null)} className="text-rose-500" data-testid={`inv-remove-${idx}`}>×</button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!readOnly && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Button variant="outline" size="sm" data-testid="add-invoice-line"
                    onClick={() => set((n) => n.invoice_lines.push({ _k: uid(), attendant_id: "", method_id: creditMethods.find((m) => !linkedTo[m.id])?.id || creditMethods[0]?.id || "",
                      customer_id: "", invoice_no: "", vehicle_reg: "", product_id: "", litres: "", amount: "" }))(null)}>
                    + Add invoice
                  </Button>
                  {customers.length === 0 && <span className="text-xs text-amber-700">Add your credit customers first in Station Setup → Customers &amp; Accounts.</span>}
                  <span className="text-xs text-muted-foreground">Litres × the shift price fills the amount; adjust it if the invoice differs.</span>
                </div>
              )}
            </>
          );
        })()}
      </Section>
      )}

      {/* Section E: Expenses */}
      <Section title="E · Expenses & Operational Payouts" icon={Receipt}>
        {/* Cash Out check: each attendant's Cash Out (Section D) must be itemized below — pick the
            attendant under "Paid out by" on each expense line it paid for */}
        {computed.cash_out_checks.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2" data-testid="cash-out-checks">
            {computed.cash_out_checks.map((c) => (
              <span key={c.attendant_id || "shop"}
                className={`rounded-lg border px-3 py-1.5 text-xs ${Math.abs(c.difference) < 0.5 ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-900"}`}>
                <span className="font-semibold">{c.attendant_name}</span>: Cash Out {kes(c.cash_out)} · itemized {kes(c.itemized)}
                {Math.abs(c.difference) < 0.5 ? " ✓" : c.difference > 0 ? ` · ${kes(c.difference)} still to itemize` : ` · itemized ${kes(-c.difference)} MORE than the Cash Out`}
              </span>
            ))}
          </div>
        )}
        <table className="recon-table">
          <thead><tr><th className="text-left">Category</th><th className="text-left">Description / Payee</th><th className="text-left">Voucher No.</th><th className="text-left">Paid out by</th><th>Amount (KES)</th><th className="w-10" /></tr></thead>
          <tbody>
            {entry.expenses.map((ex, idx) => (
              <tr key={ex._k || idx}>
                <td className="!text-left">
                  <select
                    className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly}
                    value={ex.category || ""} data-testid={`exp-cat-${idx}`}
                    onChange={(e) => set((n, v) => (n.expenses[idx].category = v))(e.target.value)}
                  >
                    <option value="">— select category —</option>
                    {(cfg.expense_categories || []).filter((c) => c.active !== false).map((c) => (
                      <option key={c.id} value={c.label}>{c.label}</option>
                    ))}
                  </select>
                </td>
                <td className="!text-left"><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={ex.description || ""} data-testid={`exp-desc-${idx}`} onChange={(e) => set((n, v) => (n.expenses[idx].description = v))(e.target.value)} /></td>
                <td className="!text-left"><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={ex.voucher_no || ""} onChange={(e) => set((n, v) => (n.expenses[idx].voucher_no = v))(e.target.value)} /></td>
                <td className="!text-left">
                  <select
                    className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly}
                    value={ex.paid_by || ""} data-testid={`exp-paid-by-${idx}`}
                    onChange={(e) => set((n, v) => (n.expenses[idx].paid_by = v || null))(e.target.value)}
                  >
                    <option value="">Office / other</option>
                    {attendants.map((a) => <option key={a.id} value={a.id}>{a.name} (Cash Out)</option>)}
                  </select>
                </td>
                <td><NumCell value={ex.amount} disabled={readOnly} tid={`exp-amount-${idx}`} onChange={set((n, v) => (n.expenses[idx].amount = v))} /></td>
                <td className="text-center">{!readOnly && <button onClick={() => set((n) => n.expenses.splice(idx, 1))(null)} className="text-rose-500" data-testid={`exp-remove-${idx}`}>×</button>}</td>
              </tr>
            ))}
            {/* The part of a Cash Out not itemized yet — keeps totals right until details are entered */}
            {computed.expense_lines.filter((x) => x.source === "cash_out_unitemized").map((x, i) => (
              <tr key={`co-${i}`} className="bg-amber-50" data-testid={`exp-cashout-unitemized-${i}`}>
                <td className="!text-left font-medium text-amber-900">Cash Out — not itemized</td>
                <td className="!text-left text-amber-900" colSpan={2}>Add expense lines with &quot;Paid out by: {x.description}&quot; to itemize this</td>
                <td className="!text-left">{x.description}</td>
                <td className="calc-cell text-amber-900">{kes(x.amount)}</td>
                <td />
              </tr>
            ))}
            <tr className="font-semibold"><td className="!text-left" colSpan={4}>Expenses Total</td><td className="calc-cell">{kes(t.expenses_total)}</td><td /></tr>
          </tbody>
        </table>
        {!readOnly && (
          <Button variant="outline" size="sm" className="mt-3" onClick={() => set((n) => n.expenses.push({ category: "", description: "", voucher_no: "", paid_by: null, amount: "", _k: uid() }))(null)} data-testid="add-expense-button">
            + Add Expense
          </Button>
        )}
        <div className="mt-4 space-y-1.5">
          <Label>Shift Notes</Label>
          <Textarea disabled={readOnly} value={entry.notes} onChange={(e) => set((n, v) => (n.notes = v))(e.target.value)} data-testid="shift-notes" placeholder="Any handover notes or exceptions…" />
        </div>
      </Section>

      {/* Section F: M-Pesa till reconciliation — same model as tank dips: opening carries forward,
          M-Pesa received is pulled from the assigned attendant's M-Pesa total in Section D (never
          re-entered), withdrawal is the only manual input, closing is computed. Actual Balance is
          an optional physical check (e.g. dialing the till) — same role as a tank's physical dip —
          producing a variance against the calculated closing, since payment and recorded
          consumption can land at slightly different times. */}
      {entry.till_ledger.length > 0 && (
        <Section title="F · M-Pesa Till Reconciliation" icon={Smartphone}>
          <div className="overflow-x-auto">
            <table className="recon-table">
              <thead>
                <tr>
                  <th className="text-left">Till</th>
                  <th className="text-left">Attendants</th>
                  <th>Opening Balance</th><th>M-Pesa Received</th><th>Withdrawal</th><th>Closing Balance</th>
                  <th>Closing Balance (phone)</th>
                  <th title="When the closing balance was read off the phone — the cut-off between this shift's and the next shift's M-Pesa payments">Reading Time</th>
                  <th>Variance</th><th className="text-left">Status</th>
                  <th className="bg-emerald-50" title="Payments Safaricom confirmed into this till since the previous shift's reading">Safaricom Received</th>
                  <th className="bg-emerald-50" title="Attendant-declared M-Pesa minus Safaricom-confirmed">Declared − Safaricom</th>
                  <th className="bg-emerald-50" title="Opening + Safaricom payments − closing reading: what must have left the till (withdrawals + charges). Compared with the Withdrawal entered.">Implied Withdrawal</th>
                </tr>
              </thead>
              <tbody>
                {entry.till_ledger.map((r, idx) => {
                  const till = tillById[r.till_id];
                  const line = computed.till_lines.find((l) => l.till_id === r.till_id) || {};
                  return (
                    <tr key={r.till_id}>
                      <td className="!text-left font-medium">{till?.label} <span className="text-xs text-muted-foreground">({till?.till_number})</span></td>
                      <td className="!text-left">
                        {/* A till can be shared — each assigned attendant's M-Pesa (Section D) adds to it */}
                        <div className="flex max-w-[22rem] flex-wrap gap-1" data-testid={`till-attendant-${r.till_id}`}>
                          {attendants.map((a) => {
                            const selected = tillAttendantList(entry.till_attendants?.[r.till_id]);
                            const on = selected.includes(a.id);
                            if (readOnly && !on) return null;
                            return (
                              <button
                                key={a.id} type="button" disabled={readOnly}
                                className={`rounded-full border px-2 py-0.5 text-xs ${on ? "border-brand bg-brand text-white" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}
                                data-testid={`till-attendant-${r.till_id}-${a.id}`}
                                onClick={() => set((n) => {
                                  n.till_attendants = { ...(n.till_attendants || {}) };
                                  const cur = tillAttendantList(n.till_attendants[r.till_id]);
                                  n.till_attendants[r.till_id] = on ? cur.filter((x) => x !== a.id) : [...cur, a.id];
                                })(null)}
                              >
                                {a.name}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td><NumCell value={r.opening_balance} disabled={readOnly || r._openingLocked} tid={`till-opening-${r.till_id}`} onChange={set((n, v) => (n.till_ledger[idx].opening_balance = v))} /></td>
                      <td className="calc-cell" title={line.wrong_till_in || line.wrong_till_out || line.account_in ? `Declared by attendants ${kes(line.declared_mpesa)}` : undefined}>
                        {kes(line.mpesa_received)}
                        {(line.wrong_till_in > 0 || line.wrong_till_out > 0 || line.account_in > 0) && (
                          <div className="text-[11px] text-sky-700">
                            {line.wrong_till_in > 0 && `+${kes(line.wrong_till_in)} wrong-till in `}
                            {line.wrong_till_out > 0 && `−${kes(line.wrong_till_out)} paid to another till `}
                            {line.account_in > 0 && `+${kes(line.account_in)} account payments`}
                          </div>
                        )}
                      </td>
                      <td><NumCell value={r.withdrawal} disabled={readOnly} tid={`till-withdrawal-${r.till_id}`} onChange={set((n, v) => (n.till_ledger[idx].withdrawal = v))} /></td>
                      <td className="calc-cell font-semibold">{kes(line.closing_balance)}</td>
                      <td><NumCell value={r.actual_balance} disabled={readOnly} tid={`till-actual-${r.till_id}`} onChange={set((n, v) => {
                        n.till_ledger[idx].actual_balance = v;
                        // stamp the reading time when a closing balance is first typed; correct it if read earlier
                        if (v !== "" && !n.till_ledger[idx].actual_balance_at) n.till_ledger[idx].actual_balance_at = nowLocalMinute();
                      })} /></td>
                      <td>
                        <input
                          type="datetime-local" className="grid-input !w-44 !text-left" style={{ textAlign: "left" }} disabled={readOnly}
                          value={r.actual_balance_at || ""} data-testid={`till-actual-at-${r.till_id}`}
                          onChange={(e) => set((n, v) => (n.till_ledger[idx].actual_balance_at = v))(e.target.value)}
                        />
                      </td>
                      <td className="calc-cell">{line.variance === null || line.variance === undefined ? "—" : `${line.variance >= 0 ? "+" : ""}${kes(line.variance)}`}</td>
                      <td className="!text-left">{line.status ? <VarianceBadge status={line.status}>{line.status}</VarianceBadge> : <span className="text-xs text-muted-foreground">—</span>}</td>
                      <td className="calc-cell" data-testid={`till-api-${r.till_id}`}
                        title={line.api_window ? `Payments from ${line.api_window[0].replace("T", " ")} to ${line.api_window[1].replace("T", " ")}` : undefined}>
                        {line.api_received == null ? "—" : <>{kes(line.api_received)} <span className="text-xs text-muted-foreground">({line.api_count})</span></>}
                      </td>
                      <td className="calc-cell">
                        {line.api_variance == null ? "—" : (
                          <VarianceBadge status={line.api_status}>{`${line.api_variance >= 0 ? "+" : ""}${kes(line.api_variance)}`}</VarianceBadge>
                        )}
                      </td>
                      <td className="calc-cell" data-testid={`till-implied-wd-${r.till_id}`}>
                        {line.implied_withdrawal == null ? "—" : (
                          <span title={`Withdrawal entered: ${kes(num(r.withdrawal))}`}>
                            {kes(line.implied_withdrawal)}
                            {Math.abs(line.withdrawal_gap) > 0.5 && (
                              <span className={`ml-1 text-xs ${Math.abs(line.withdrawal_gap) > 100 ? "text-rose-600" : "text-amber-600"}`}>
                                ({line.withdrawal_gap > 0 ? "+" : ""}{kes(line.withdrawal_gap)} unrecorded)
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Visa and Shell Card per attendant: each total broken into the receipts behind it */}
          {(computed.card_checks || []).length > 0 && (
            <div className="mt-5" data-testid="card-itemization">
              <h4 className="text-sm font-semibold text-slate-800">Visa &amp; Shell Card receipts by attendant</h4>
              <p className="text-xs text-muted-foreground">
                Each attendant&apos;s card total, receipt by receipt. Visa: date, receipt no., authorization code and amount. Shell Card: date, product, receipt no. and amount.
              </p>
              {t.card_itemize_required && (
                <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs font-medium text-amber-900">
                  Required from {t.card_itemize_from}: this shift can&apos;t be submitted until every Visa and Shell Card total is fully itemized.
                </p>
              )}
              <div className="mt-2 space-y-3">
                {computed.card_checks.map((c) => {
                  const name = attendants.find((a) => a.id === c.attendant_id)?.name || "Unassigned";
                  const rows = entry.payment_transactions.map((tx, idx) => ({ ...tx, idx }))
                    .filter((tx) => (tx.attendant_id || "") === (c.attendant_id || "") && tx.method_id === c.method_id);
                  const ok = Math.abs(c.difference) < 0.5;
                  const FLAG = { no_receipt: "receipt no. missing", duplicate: "receipt entered twice", no_auth: "authorization code missing", no_product: "product missing" };
                  const shell = c.card === "shell";
                  return (
                    <div key={`${c.attendant_id}|${c.method_id}`} className="overflow-hidden rounded-lg border" data-testid={`card-att-${c.attendant_id}-${c.card}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-slate-50 px-3 py-2 text-sm">
                        <span className="font-semibold text-slate-800">{name} · {c.method_name}</span>
                        <span className="text-xs text-muted-foreground">
                          Declared <b className="font-mono-fin text-slate-700">{kes(c.declared)}</b> · itemized <b className="font-mono-fin text-slate-700">{kes(c.itemized)}</b> ({c.count})
                          <span className={`ml-2 font-semibold ${ok ? "text-emerald-600" : c.difference > 0 ? "text-amber-600" : "text-rose-600"}`}>
                            {ok ? "✓" : c.difference > 0 ? `${kes(c.difference)} still to itemize` : `itemized ${kes(-c.difference)} more than declared`}
                          </span>
                        </span>
                      </div>
                      <div className="space-y-1.5 p-2">
                        {rows.map((tx, i) => {
                          const chk = c.rows[i] || { flags: [] };
                          return (
                            <div key={tx._k || tx.idx} className="flex flex-wrap items-center gap-2">
                              <Input type="date" className="w-40" disabled={readOnly} value={tx.txn_date || ""} title="Date"
                                onChange={(e) => set((n, v) => (n.payment_transactions[tx.idx].txn_date = v))(e.target.value)} />
                              {shell && (
                                <select className={`h-10 rounded-md border bg-white px-2 text-sm ${chk.flags.includes("no_product") ? "border-rose-300 bg-rose-50" : ""}`} disabled={readOnly} value={tx.product || ""}
                                  onChange={(e) => set((n, v) => (n.payment_transactions[tx.idx].product = v))(e.target.value)}>
                                  <option value="">Product…</option>
                                  {["Diesel", "Super", "V-Power", "Lubricant"].map((p) => <option key={p} value={p}>{p}</option>)}
                                </select>
                              )}
                              <Input className={`w-36 font-mono uppercase ${chk.flags.some((f) => f === "no_receipt" || f === "duplicate") ? "border-rose-300 bg-rose-50" : ""}`} disabled={readOnly} value={tx.reference || ""} placeholder="Receipt no."
                                onChange={(e) => set((n, v) => (n.payment_transactions[tx.idx].reference = v.toUpperCase()))(e.target.value)} />
                              {!shell && (
                                <Input className={`w-36 font-mono uppercase ${chk.flags.includes("no_auth") ? "border-rose-300 bg-rose-50" : ""}`} disabled={readOnly} value={tx.auth_code || ""} placeholder="Authorization code"
                                  onChange={(e) => set((n, v) => (n.payment_transactions[tx.idx].auth_code = v.toUpperCase()))(e.target.value)} />
                              )}
                              <Input type="number" className="w-32 font-mono-fin" disabled={readOnly} value={tx.amount} placeholder="Amount"
                                onChange={(e) => set((n, v) => (n.payment_transactions[tx.idx].amount = v))(e.target.value)} />
                              {chk.flags.length > 0 && <span className="text-xs text-rose-600">{chk.flags.map((f) => FLAG[f] || f).join(" · ")}</span>}
                              {!readOnly && <button className="ml-auto text-rose-500" onClick={() => set((n) => n.payment_transactions.splice(tx.idx, 1))(null)}>×</button>}
                            </div>
                          );
                        })}
                        {!readOnly && (
                          <Button variant="outline" size="sm" className="h-7" data-testid={`card-add-${c.attendant_id}-${c.card}`}
                            onClick={() => set((n) => n.payment_transactions.push({ attendant_id: c.attendant_id, method_id: c.method_id, amount: "", reference: "", note: "",
                              txn_date: shift?.date || "", auth_code: "", product: "", _k: uid() }))(null)}>+ Add receipt</Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Wrong-till payments: a customer paid into one till what belonged to another. The amount
              moves from the till it was meant for to the one it landed in, so both reconcile; the
              attendant isn't affected (they declared it). Suggested when one till is over by about
              what another is short — always confirmed by a person, with the M-Pesa code. */}
          <div className="mt-5">
            <h4 className="text-sm font-semibold text-slate-800">Wrong-till payments</h4>
            <p className="text-xs text-muted-foreground">A customer paid into the wrong till. When one till is over by what another is short, confirm it and both tills balance. No code is needed.</p>
            {!readOnly && (computed.till_transfer_suggestions || []).filter((sg) =>
              !(entry.till_transfers || []).some((t) => sg.reference ? mpesaCode(t.reference) === sg.reference : (t.landed_till_id === sg.landed_till_id && t.meant_till_id === sg.meant_till_id))).map((sg) => (
              <div key={`${sg.landed_till_id}-${sg.meant_till_id}-${sg.reference || ""}`} className="mt-2 flex flex-wrap items-center gap-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="wrong-till-suggestion">
                <span>
                  {sg.source === "safaricom"
                    ? <>Safaricom shows M-Pesa <b>{sg.reference}</b> ({kes(sg.amount)}) in <b>{tillById[sg.landed_till_id]?.label}</b>, but the attendant who claimed it uses <b>{tillById[sg.meant_till_id]?.label}</b>.</>
                    : <><b>{tillById[sg.landed_till_id]?.label}</b> is over by {kes(sg.amount)} and <b>{tillById[sg.meant_till_id]?.label}</b> is short by {kes(-sg.short)} —
                  looks like a payment meant for {tillById[sg.meant_till_id]?.label} landed in {tillById[sg.landed_till_id]?.label}.</>}
                  {Math.abs(sg.leftover) > 0.5 && ` (${kes(Math.abs(sg.leftover))} would still be left as a real difference.)`}
                </span>
                <Button size="sm" className="h-7 bg-brand hover:bg-brand/90" data-testid="confirm-wrong-till" onClick={() => set((n) => {
                  const who = user?.name || user?.email || "supervisor";
                  const when = new Date().toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" });
                  n.till_transfers = [...(n.till_transfers || []), { _k: uid(), landed_till_id: sg.landed_till_id, meant_till_id: sg.meant_till_id,
                    amount: sg.amount, reference: sg.reference || "", note: `Confirmed by ${who}, ${when}` }];
                })(null)}>Confirm wrong-till payment</Button>
              </div>
            ))}
            {(entry.till_transfers || []).length > 0 && (
              <table className="recon-table mt-2">
                <thead><tr><th className="text-left">Landed in</th><th className="text-left">Meant for</th><th>Amount</th><th className="text-left">M-Pesa code (optional)</th><th className="text-left">Confirmed</th><th className="w-10" /></tr></thead>
                <tbody>
                  {entry.till_transfers.map((t, idx) => (
                    <tr key={t._k}>
                      {["landed_till_id", "meant_till_id"].map((f) => (
                        <td key={f} className="!text-left">
                          <select className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={t[f] || ""}
                            onChange={(e) => set((n, v) => (n.till_transfers[idx][f] = v || null))(e.target.value)}>
                            <option value="">— till —</option>
                            {entry.till_ledger.map((tl) => <option key={tl.till_id} value={tl.till_id}>{tillById[tl.till_id]?.label}</option>)}
                          </select>
                        </td>
                      ))}
                      <td><NumCell value={t.amount} disabled={readOnly} tid={`wrong-till-amount-${idx}`} onChange={set((n, v) => (n.till_transfers[idx].amount = v))} /></td>
                      <td className="!text-left"><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={t.reference || ""} placeholder="optional"
                        onChange={(e) => set((n, v) => (n.till_transfers[idx].reference = v))(e.target.value)} /></td>
                      <td className="!text-left"><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={t.note || ""}
                        onChange={(e) => set((n, v) => (n.till_transfers[idx].note = v))(e.target.value)} /></td>
                      <td className="text-center">{!readOnly && <button onClick={() => set((n) => n.till_transfers.splice(idx, 1))(null)} className="text-rose-500">×</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!readOnly && (
              <Button variant="outline" size="sm" className="mt-2" data-testid="add-wrong-till"
                onClick={() => set((n) => { n.till_transfers = [...(n.till_transfers || []), { _k: uid(), landed_till_id: null, meant_till_id: null, amount: "", reference: "", note: "" }]; })(null)}>
                + Add wrong-till payment
              </Button>
            )}
          </div>
        </Section>
      )}

      {/* G · Account payments received: a credit customer settling their account at the station —
          not sales, not attendant collections. M-Pesa adds to that till's expected balance, cash is
          banked with the shift's cash, a linked card (Visa / Shell Card) is owed by the card company;
          the customer is credited in Debtors automatically. */}
      {(() => {
        const customers = (cfg.receivable_accounts || []).filter((a) => a.kind === "customer" && (a.active !== false || (entry.account_receipts || []).some((x) => x.account_id === a.id)));
        const payMethods = [...(cfg.payment_methods || []).filter((m) => ["cash", "mobile_money", "card"].includes(m.kind) && m.active !== false)];
        const methodById = Object.fromEntries([...(cfg.payment_methods || []), ...payMethods].map((m) => [m.id, m]));
        const hints = (computed.account_receipt_hints || []).filter((h) => !(entry.account_receipts || []).some((a) => a.till_id === h.till_id));
        const mpesa = payMethods.find((m) => m.kind === "mobile_money");
        const blank = (extra = {}) => ({ _k: uid(), account_id: null, method_id: null, till_id: null, amount: "", reference: "", note: "", ...extra });
        return (
          <Section title="G · Account payments & Shell Card top-ups received" icon={Landmark}>
            <p className="text-xs text-muted-foreground">
              A credit customer (e.g. Montrose) paid their account here — by M-Pesa, cash or card. It is not a sale: M-Pesa goes into that till&apos;s expected balance,
              cash is banked with the shift&apos;s cash, and the customer&apos;s balance in Debtors goes down automatically.
              A <b>Shell Card top-up</b> (a customer loading their Shell Card here) goes here too, as &ldquo;Shell Card top-up (Vivo)&rdquo; with the card
              number as reference — Vivo takes it from the station&apos;s account, so it is a credit on the Shell Card statement, never a sale.
              Attendants don&apos;t put top-up money in their own columns.
            </p>
            {!readOnly && hints.map((h) => (
              <div key={h.till_id} className="mt-2 flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="account-receipt-hint">
                <span><b>{tillById[h.till_id]?.label}</b> is over by {kes(h.amount)} and no other till is short by the same — was this a customer paying their account?</span>
                {mpesa && <Button size="sm" variant="outline" className="h-7" onClick={() => set((n) => {
                  n.account_receipts = [...(n.account_receipts || []), blank({ method_id: mpesa.id, till_id: h.till_id, amount: h.amount })];
                })(null)}>Record as account payment</Button>}
              </div>
            ))}
            {(entry.account_receipts || []).length > 0 && (
              <table className="recon-table mt-2">
                <thead><tr><th className="text-left">Customer</th><th className="text-left">Paid by</th><th className="text-left">Into till / bank</th><th>Amount</th>
                  <th className="text-left">Reference (M-Pesa code / slip / receipt)</th><th className="text-left">Note</th><th className="w-10" /></tr></thead>
                <tbody>
                  {entry.account_receipts.map((a, idx) => {
                    const isMpesa = methodById[a.method_id]?.kind === "mobile_money";
                    const sel = (field, value, opts, placeholder, bad) => (
                      <select className={`grid-input !text-left ${bad ? "!border-rose-300 !bg-rose-50" : ""}`} style={{ textAlign: "left" }} disabled={readOnly} value={value || ""}
                        onChange={(e) => set((n, v) => {
                          n.account_receipts[idx][field] = v || null;
                          if (field === "method_id" && methodById[v]?.kind !== "mobile_money") n.account_receipts[idx].till_id = null;
                          if (field === "method_id" && !["cheque", "transfer"].includes(v)) n.account_receipts[idx].bank = null;
                        })(e.target.value)}>
                        <option value="">{placeholder}</option>
                        {opts.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                      </select>
                    );
                    return (
                      <tr key={a._k}>
                        <td className="!text-left">{sel("account_id", a.account_id, [...(cfg.receivable_accounts || []).filter((x) => x.kind === "card" && /shell/i.test(x.name || "") && x.active !== false).map((x) => [x.id, "Shell Card top-up (Vivo)"]),
                          ...customers.map((c) => [c.id, c.name])], "— customer / top-up —", !a.account_id)}</td>
                        <td className="!text-left">{sel("method_id", a.method_id, payMethods.map((m) => [m.id, m.name]), "— method —", !a.method_id)}</td>
                        <td className="!text-left">{isMpesa ? sel("till_id", a.till_id, entry.till_ledger.map((tl) => [tl.till_id, tillById[tl.till_id]?.label]), "— till —", !a.till_id)
                          : ["cheque", "transfer"].includes(a.method_id) ? sel("bank", a.bank, RECEIPT_BANKS.map((b) => [b, b]), "— bank —", !a.bank)
                          : <span className="text-xs text-muted-foreground">—</span>}</td>
                        <td><NumCell value={a.amount} disabled={readOnly} tid={`acct-amount-${idx}`} onChange={set((n, v) => (n.account_receipts[idx].amount = v))} /></td>
                        <td className="!text-left"><input className={`grid-input !text-left ${!a.reference && !a.note ? "!border-rose-300 !bg-rose-50" : ""}`} style={{ textAlign: "left" }} disabled={readOnly} value={a.reference || ""}
                          onChange={(e) => set((n, v) => (n.account_receipts[idx].reference = v))(e.target.value)} /></td>
                        <td className="!text-left"><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={a.note || ""}
                          onChange={(e) => set((n, v) => (n.account_receipts[idx].note = v))(e.target.value)} /></td>
                        <td className="text-center">{!readOnly && <button onClick={() => set((n) => n.account_receipts.splice(idx, 1))(null)} className="text-rose-500">×</button>}</td>
                      </tr>
                    );
                  })}
                  <tr className="font-semibold"><td className="!text-left" colSpan={3}>Total account payments</td><td className="calc-cell">{kes(t.account_receipts_total)}</td><td colSpan={3} /></tr>
                </tbody>
              </table>
            )}
            {!readOnly && (
              <Button variant="outline" size="sm" className="mt-2" data-testid="add-account-receipt"
                onClick={() => set((n) => { n.account_receipts = [...(n.account_receipts || []), blank()]; })(null)}>
                + Add account payment
              </Button>
            )}
          </Section>
        );
      })()}
        </TabsContent>
      </Tabs>

      {/* Sticky action bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/90 backdrop-blur-md shadow-lg">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {saveError
              ? <span className="font-semibold text-rose-600" data-testid="save-error">● {saveError}</span>
              : dirty ? <span className="text-amber-600">● Unsaved changes</span> : <span>All changes saved</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={openAudit} data-testid="audit-drawer-toggle"><History className="mr-1.5 h-4 w-4" />Audit</Button>
            {readOnly && <Button variant="outline" size="sm" onClick={openAdjustments} data-testid="corrections-button"><ShieldAlert className="mr-1.5 h-4 w-4" />Corrections</Button>}
            <Button variant="outline" size="sm" onClick={() => download("excel")} data-testid="export-excel-button"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Excel</Button>
            <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="export-pdf-button"><FileText className="mr-1.5 h-4 w-4" />PDF</Button>

            {!readOnly && (
              <>
                <Button size="sm" variant="secondary" onClick={save} disabled={saving} data-testid="save-draft-button">
                  {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Save Draft
                </Button>
                <Button size="sm" className="bg-brand hover:bg-brand/90" onClick={() => setTransition({ action: "submit", title: "Submit Shift" })} data-testid="submit-shift-button">
                  <Send className="mr-1.5 h-4 w-4" />Submit
                </Button>
              </>
            )}
            {shift.status === "submitted" && isManager && (
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => setTransition({ action: "manager-review", title: "Confirm Manager Review" })} data-testid="manager-review-button">
                <ClipboardCheck className="mr-1.5 h-4 w-4" />Manager Review
              </Button>
            )}
            {shift.status === "manager_reviewed" && isAccountant && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setTransition({ action: "accountant-approve", title: "Accountant Approval" })} data-testid="accountant-approve-button">
                <CheckCircle2 className="mr-1.5 h-4 w-4" />Approve
              </Button>
            )}
            {shift.status === "accountant_approved" && isAccountant && (
              <Button size="sm" className="bg-indigo-950 hover:bg-indigo-900" onClick={() => setTransition({ action: "lock", title: "Lock Shift" })} data-testid="lock-shift-button">
                <Lock className="mr-1.5 h-4 w-4" />Lock
              </Button>
            )}
            {readOnly && isAccountant && (
              <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setTransition({ action: "reopen", title: "Reopen Shift (Audited)" })} data-testid="reopen-shift-button">
                <Unlock className="mr-1.5 h-4 w-4" />Reopen
              </Button>
            )}
          </div>
        </div>
      </div>

      <AuditTrailDrawer open={auditOpen} onOpenChange={setAuditOpen} logs={logs} />

      {/* Transition confirm dialog */}
      <Dialog open={!!transition} onOpenChange={(o) => !o && setTransition(null)}>
        <DialogContent data-testid="transition-dialog">
          <DialogHeader><DialogTitle className="font-display">{transition?.title}</DialogTitle><DialogDescription>This action is recorded in the audit trail.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              {transition?.action === "reopen"
                ? "Reopening returns the shift to draft and is fully audited. A reason is mandatory."
                : transition?.action === "submit" && t.cash_status === "critical"
                  ? `Cash variance is KSh ${kes(t.cash_variance)} — beyond the limit. Explain it below (e.g. who is short and how it will be recovered); the note is required to submit.`
                  : "Add an optional note. This action is recorded in the audit trail."}
            </p>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={transition?.action === "reopen" ? "Reason for reopening (required)" : "Note (optional)"} data-testid="transition-note" />
            {transition?.action === "submit" && isAccountant && (
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input type="checkbox" className="mt-0.5" checked={emailOnSubmit} onChange={(e) => setEmailOnSubmit(e.target.checked)} data-testid="transition-email" />
                <span>Email the shift report<span className="block text-xs text-muted-foreground">Untick when resubmitting a corrected shift, so the report isn&apos;t sent out again.</span></span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTransition(null)}>Cancel</Button>
            <Button onClick={runTransition} disabled={busy || (transition?.action === "reopen" && !note.trim())} className="bg-brand hover:bg-brand/90" data-testid="transition-confirm">
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdjustmentsDialog open={adjOpen} onOpenChange={setAdjOpen} shiftId={id} adjustments={adjustments} reload={openAdjustments} user={user} shift={shift} cfg={cfg} />
    </div>
  );
}

function num(v) {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

// A row's opening value is locked once it's actually been set (carried forward from the previous
// shift, or saved once before) — only a brand-new pump/tank/item/till with no history yet is
// still blank and editable. Prevents quietly tampering with the reconciliation baseline; use
// Corrections (audited + approved) for a genuine fix.
function hasValue(v) {
  return v !== undefined && v !== null && v !== "";
}

// Current local time as a datetime-local value ("YYYY-MM-DDTHH:MM") — the station runs on EAT
// banks a customer's cheque or transfer can be paid into (the books match the bank by this name)
const RECEIPT_BANKS = ["Standard Chartered 4700", "Standard Chartered 4702"];

function nowLocalMinute() {
  const d = new Date();
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// till_attendants values are lists; older shifts stored a single attendant id string
function tillAttendantList(v) {
  return (Array.isArray(v) ? v : (v ? [v] : [])).filter(Boolean);
}

function uid() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random());
}

function NumCell({ value, onChange, disabled, tid }) {
  return <input type="number" className="grid-input" value={value} disabled={disabled} data-testid={tid} onChange={(e) => onChange(e.target.value)} />;
}

function Section({ title, icon: Icon, children }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b bg-slate-50 px-5 py-3">
        <Icon className="h-4 w-4 text-brand" />
        <h3 className="font-display text-base font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

function SummaryStat({ label, value, icon: Icon, status }) {
  const color = status === "critical" ? "text-rose-600" : status === "warning" ? "text-amber-600" : "text-slate-900";
  return (
    <Card className="p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className={`mt-1 font-mono-fin text-sm font-bold ${color}`}>{value}</div>
    </Card>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`font-mono-fin ${bold ? "font-bold text-slate-900" : "text-slate-700"}`}>{value}</dd>
    </div>
  );
}

// Defines what can be corrected: how to list candidate rows for a section (matched by a stable
// id, never array index, since a row's position can shift) and which fields of that row are
// editable. Drives the structured correction picker below.
const ADJUSTMENT_SECTIONS = {
  fuel_readings: {
    label: "Fuel Pump Reading",
    rows: (shift, cfg) => (shift.fuel_readings || []).map((row) => ({
      match: { pump_id: row.pump_id }, row,
      label: cfg.pumps.find((p) => p.id === row.pump_id)?.label || row.pump_id,
    })),
    fields: [
      { key: "opening", label: "Manual Opening (L)" }, { key: "closing", label: "Manual Closing (L)" },
      { key: "testing", label: "RTT (L)" },
      { key: "elec_opening_l", label: "Electronic Opening (L)" }, { key: "elec_closing_l", label: "Electronic Closing (L)" },
      { key: "elec_opening_kes", label: "Electronic Opening (KES)" }, { key: "elec_closing_kes", label: "Electronic Closing (KES)" },
    ],
  },
  tank_dips: {
    label: "Tank Dip",
    rows: (shift, cfg) => (shift.tank_dips || []).map((row) => ({
      match: { tank_id: row.tank_id }, row,
      label: cfg.tanks.find((t) => t.id === row.tank_id)?.label || row.tank_id,
    })),
    fields: [
      { key: "opening_dip", label: "Opening Dip" }, { key: "deliveries", label: "Deliveries" },
      { key: "rtt", label: "Other returns" }, { key: "closing_dip", label: "Closing Dip" },
      { key: "tls_opening_dip", label: "TLS Opening Dip" }, { key: "tls_closing_dip", label: "TLS Closing Dip" },
    ],
  },
  nonfuel: {
    label: "Non-Fuel Stock",
    rows: (shift, cfg) => (shift.nonfuel || []).map((row) => ({
      match: { item_id: row.item_id }, row,
      label: cfg.nonfuel_items.find((i) => i.id === row.item_id)?.name || row.item_id,
    })),
    fields: [
      { key: "opening", label: "Opening" }, { key: "received", label: "Received" }, { key: "returned", label: "Returned (LPG)" },
      { key: "closing", label: "Closing" }, { key: "shop_opening", label: "Opening (shop)" }, { key: "shop_closing", label: "Closing (shop)" },
      { key: "unit_price", label: "Unit Price" }, { key: "cost_price", label: "Cost Price" },
    ],
  },
  payments: {
    label: "Attendant Payment (Section D)",
    rows: (shift, cfg) => (shift.payments || []).map((row) => ({
      match: { attendant_id: row.attendant_id || null, method_id: row.method_id }, row,
      label: `${cfg.attendants.find((a) => a.id === row.attendant_id)?.name || "Unassigned / Shop"} — ${cfg.payment_methods.find((m) => m.id === row.method_id)?.name || row.method_id}`,
    })),
    fields: [{ key: "amount", label: "Amount" }, { key: "reference", label: "Reference" }],
  },
  nonfuel_payments: {
    label: "Non-Fuel Payment (Lubes/LPG)",
    rows: (shift, cfg) => (shift.nonfuel_payments || []).map((row) => ({
      match: row._k ? { _k: row._k } : { group: row.group, method_id: row.method_id }, row,
      label: `${cfg.attendants.find((a) => a.id === row.attendant_id)?.name || "—"} · ${row.group === "lube" ? "Lubes" : "LPG & Accessories"} — ${cfg.payment_methods.find((m) => m.id === row.method_id)?.name || row.method_id}`,
    })),
    fields: [{ key: "amount", label: "Amount" }, { key: "reference", label: "Reference" }],
  },
  till_ledger: {
    label: "Till Reconciliation",
    rows: (shift, cfg) => (shift.till_ledger || []).map((row) => ({
      match: { till_id: row.till_id }, row,
      label: cfg.tills.find((t) => t.id === row.till_id)?.label || row.till_id,
    })),
    fields: [
      { key: "opening_balance", label: "Opening Balance" }, { key: "withdrawal", label: "Withdrawal" },
      { key: "actual_balance", label: "Closing Balance (phone)" }, { key: "actual_balance_at", label: "Reading Time" },
    ],
  },
  account_receipts: {
    label: "Account payment received",
    rows: (shift, cfg) => (shift.account_receipts || []).map((row) => ({
      match: { _k: row._k }, row,
      label: `${(cfg.receivable_accounts || []).find((a) => a.id === row.account_id)?.name || "?"} — ${cfg.payment_methods.find((m) => m.id === row.method_id)?.name || "?"}`,
    })),
    fields: [{ key: "amount", label: "Amount" }, { key: "reference", label: "Reference" }, { key: "note", label: "Note" }],
  },
  till_transfers: {
    label: "Wrong-till payment",
    rows: (shift, cfg) => (shift.till_transfers || []).map((row) => ({
      match: { _k: row._k }, row,
      label: `${cfg.tills.find((t) => t.id === row.landed_till_id)?.label || "?"} ← meant for ${cfg.tills.find((t) => t.id === row.meant_till_id)?.label || "?"}`,
    })),
    fields: [{ key: "amount", label: "Amount" }, { key: "reference", label: "M-Pesa code" }, { key: "note", label: "Note" }],
  },
  invoice_lines: {
    label: "Invoice line",
    rows: (shift, cfg) => (shift.invoice_lines || []).map((row) => ({
      match: { _k: row._k }, row,
      label: `${cfg.attendants.find((a) => a.id === row.attendant_id)?.name || "—"} · ${row.invoice_no || "invoice"} · ${row.vehicle_reg || ""}`,
    })),
    fields: [{ key: "invoice_no", label: "Invoice / LPO no." }, { key: "vehicle_reg", label: "Vehicle reg" },
      { key: "litres", label: "Litres" }, { key: "amount", label: "Amount" }],
  },
  expenses: {
    label: "Expense",
    rows: (shift) => (shift.expenses || []).map((row, idx) => ({
      match: { _k: row._k || String(idx) }, row,
      label: `${row.category || "—"} — ${row.description || ""}`,
    })),
    fields: [
      { key: "category", label: "Category" }, { key: "description", label: "Description" },
      { key: "voucher_no", label: "Voucher No." }, { key: "amount", label: "Amount" },
    ],
  },
  tank_deliveries: {
    label: "Fuel Delivery",
    rows: (shift, cfg) => (shift.tank_deliveries || []).map((row, idx) => ({
      match: { _k: row._k || String(idx) }, row,
      label: `${cfg.tanks.find((t) => t.id === row.tank_id)?.label || row.tank_id} — Inv# ${row.invoice_no || "—"}`,
    })),
    fields: [
      { key: "invoice_no", label: "Invoice No." }, { key: "date", label: "Date" },
      { key: "invoice_amount", label: "Invoice Amount" }, { key: "invoice_volume", label: "Invoice Volume" },
      { key: "offloaded_quantity", label: "Offloaded Quantity" },
    ],
  },
  nonfuel_deliveries: {
    label: "Dry Stock Delivery",
    rows: (shift, cfg) => (shift.nonfuel_deliveries || []).map((row, idx) => ({
      match: { _k: row._k || String(idx) }, row,
      label: `${cfg.nonfuel_items.find((i) => i.id === row.item_id)?.name || row.item_id} — Inv# ${row.invoice_no || "—"}`,
    })),
    fields: [
      { key: "invoice_no", label: "Invoice No." }, { key: "date", label: "Date" },
      { key: "invoice_amount", label: "Invoice Amount" }, { key: "invoice_quantity", label: "Invoice Quantity" },
      { key: "actual_quantity", label: "Actual Quantity" },
    ],
  },
};

function AdjustmentsDialog({ open, onOpenChange, shiftId, adjustments, reload, user, shift, cfg }) {
  const [sectionKey, setSectionKey] = useState("fuel_readings");
  const [rowIdx, setRowIdx] = useState("");
  const [fieldKey, setFieldKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const isAccountant = canAccount(user);

  const sectionDef = ADJUSTMENT_SECTIONS[sectionKey];
  const rows = shift && cfg ? sectionDef.rows(shift, cfg) : [];
  const selectedRow = rowIdx !== "" ? rows[Number(rowIdx)] : null;
  const fieldDef = sectionDef.fields.find((f) => f.key === fieldKey);
  const originalValue = selectedRow && fieldKey ? selectedRow.row[fieldKey] : "";

  const resetPickers = (nextSection) => {
    setSectionKey(nextSection);
    setRowIdx(""); setFieldKey(""); setNewValue("");
  };

  const submit = async () => {
    if (!selectedRow || !fieldDef || !reason.trim()) return toast.error("Pick a row, a field, and give a reason");
    setBusy(true);
    try {
      await api.post(`/shifts/${shiftId}/adjustments`, {
        field_label: `${sectionDef.label}: ${selectedRow.label} → ${fieldDef.label}`,
        section: sectionKey, match: selectedRow.match, field: fieldKey,
        original_value: originalValue, new_value: newValue, reason,
      });
      toast.success("Correction request raised");
      setRowIdx(""); setFieldKey(""); setNewValue(""); setReason("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (adjId, decision) => {
    try {
      await api.post(`/shifts/${shiftId}/adjustments/${adjId}/decide`, { decision });
      toast.success(decision === "approve" ? "Correction approved and applied to the shift" : "Correction rejected");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="adjustments-dialog">
        <DialogHeader><DialogTitle className="font-display">Controlled Corrections</DialogTitle><DialogDescription>Pick exactly what to correct; an accountant approves it, and approving writes the change straight into the shift. Fully audited.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border bg-slate-50 p-4">
            <h4 className="mb-3 text-sm font-semibold text-slate-800">Raise a correction request</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Section</Label>
                <select className="grid-input !text-left" style={{ textAlign: "left" }} value={sectionKey} data-testid="adj-section-select" onChange={(e) => resetPickers(e.target.value)}>
                  {Object.entries(ADJUSTMENT_SECTIONS).map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Row</Label>
                <select className="grid-input !text-left" style={{ textAlign: "left" }} value={rowIdx} data-testid="adj-row-select" onChange={(e) => { setRowIdx(e.target.value); setFieldKey(""); setNewValue(""); }}>
                  <option value="">— select —</option>
                  {rows.map((r, i) => <option key={i} value={i}>{r.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Field</Label>
                <select className="grid-input !text-left" style={{ textAlign: "left" }} value={fieldKey} disabled={!selectedRow} data-testid="adj-field-select" onChange={(e) => { setFieldKey(e.target.value); setNewValue(""); }}>
                  <option value="">— select —</option>
                  {sectionDef.fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Original value</Label><Input value={String(originalValue ?? "—")} disabled data-testid="adj-original-input" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs">Proposed value</Label><Input value={newValue} onChange={(e) => setNewValue(e.target.value)} disabled={!fieldDef} data-testid="adj-new-input" /></div>
            </div>
            <div className="mt-3 space-y-1.5"><Label className="text-xs">Reason (required)</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adj-reason-input" /></div>
            <Button onClick={submit} disabled={busy} className="mt-3 bg-brand hover:bg-brand/90" size="sm" data-testid="adj-submit-button">
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Submit request
            </Button>
          </div>

          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-800">Correction history</h4>
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {adjustments.length === 0 && <p className="text-sm text-muted-foreground">No correction requests.</p>}
              {adjustments.map((a) => (
                <div key={a._id} className="rounded-lg border p-3 text-sm" data-testid={`adjustment-${a._id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{a.field_label}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${a.status === "approved" ? "bg-emerald-100 text-emerald-800" : a.status === "rejected" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{a.status}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <span className="font-mono-fin text-rose-600 line-through">{String(a.original_value ?? "—")}</span>→
                    <span className="font-mono-fin text-emerald-700">{String(a.new_value ?? "—")}</span>
                  </div>
                  <div className="mt-1 text-xs italic text-slate-600">"{a.reason}"</div>
                  <div className="mt-1 text-[11px] text-muted-foreground">by {a.requested_by}</div>
                  {a.status === "pending" && isAccountant && (
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(a._id, "approve")} data-testid={`adj-approve-${a._id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="h-7 border-rose-300 text-rose-700" onClick={() => decide(a._id, "reject")} data-testid={`adj-reject-${a._id}`}>Reject</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
